/**
 * GO OIL — Enterprise Coupon & Reward Engine (Frontend)
 * ─────────────────────────────────────────────────────
 *
 *  Owner       → Batches / Coupons / Redemptions / Credit Notes / Dispatch Advices /
 *                Reports / Audit Log
 *  Accountant  → Redemptions / Credit Notes (approval flow)
 *  Sales Off.  → Retailer picker + Scan flow (camera + manual)
 *  Retailer    → Cash Wallet + Reward Wallet + History (VIEW ONLY — no scan)
 *  Distributor → Retailer wallets + Credit Notes + Dispatch Advices (VIEW ONLY)
 *  Team Leader → Salesperson performance reports (VIEW ONLY)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { dms, niceDate, inr } from "./api";
import { PageHeader } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import QrScanner from "./QrScanner";
import BatchScan from "./BatchScan";
import {
  Ticket, Plus, Shield, ShieldAlert, ScanLine, CheckCircle2, XCircle, Trophy,
  Users, Store, Award, RefreshCw, PackageCheck, Printer, FileSpreadsheet,
  FileText, Wallet, Coins, Truck, Search, ArrowRight, ChevronLeft, ChevronRight,
  Play, PauseCircle, Copy, AlertTriangle, ClipboardList, Activity, TrendingUp,
  Download, Share2, Eye, EyeOff, Lock, MessageCircle, Link2, Clock, Loader2,
  Trash2,
} from "lucide-react";

// ═════════════════════════ shared bits ══════════════════════════════════════
const GOLD_BTN = "bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white";

function Kpi({ label, value, tint = "bg-slate-100 text-slate-700", icon: Icon = Ticket, suffix, onClick, testid }) {
  const display = typeof value === "number" ? value.toLocaleString("en-IN") : (value ?? 0);
  const clickable = typeof onClick === "function";
  return (
    <Card
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick?.(); } : undefined}
      data-testid={testid}
      className={`p-4 group relative ${clickable ? "cursor-pointer hover:shadow-md hover:border-amber-300 hover:-translate-y-0.5 transition-all" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className={`inline-flex h-9 w-9 rounded-lg items-center justify-center mb-2 ${tint}`}>
          <Icon size={18} />
        </div>
        {clickable && (
          <ChevronRight size={14} className="text-slate-300 group-hover:text-[#a67c00] group-hover:translate-x-0.5 transition-all" />
        )}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold text-slate-900 mt-1 ${clickable ? "group-hover:text-[#8a6600] transition-colors" : ""}`}>
        {display}{suffix && <span className="text-sm text-slate-500 ml-1">{suffix}</span>}
      </div>
    </Card>
  );
}

const STATUS_STYLES = {
  generated: "bg-slate-100 text-slate-700",
  activated: "bg-blue-100 text-blue-800",
  printed: "bg-indigo-100 text-indigo-800",
  issued_to_production: "bg-violet-100 text-violet-800",
  unused: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  claimed: "bg-amber-100 text-amber-800",
  redemption_pending: "bg-orange-100 text-orange-800",
  redeemed: "bg-emerald-100 text-emerald-800",
  expired: "bg-slate-200 text-slate-600",
  cancelled: "bg-rose-100 text-rose-800",
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  dispatched: "bg-indigo-100 text-indigo-800",
  issued: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
};
function StatusChip({ s }) {
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[s] || "bg-slate-100 text-slate-700"}`}>
      {(s || "").replace(/_/g, " ")}
    </span>
  );
}

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-4">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${
            value === t.id
              ? "border-[#a67c00] text-[#8a6600]"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          {t.label}{t.badge != null && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
              {t.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ═════════════════════════ OWNER: Coupon Dashboard (Batches + KPIs) ═════════
export function OwnerCouponsPage() {
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [scanPerm, setScanPerm] = useState(false);
  const [permBusy, setPermBusy] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [restoreBusyId, setRestoreBusyId] = useState(null);
  const [deletedBatches, setDeletedBatches] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    dms.cpnGetScanPermission().then(p => setScanPerm(!!p.retailer_scan_enabled)).catch(() => {});
  }, []);
  const toggleScanPerm = async () => {
    setPermBusy(true);
    try {
      const next = !scanPerm;
      const r = await dms.cpnSetScanPermission(next);
      setScanPerm(!!r.retailer_scan_enabled);
      toast.success(next ? "Retailer scanning enabled" : "Retailer scanning disabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPermBusy(false); }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, b, db_] = await Promise.all([
        dms.cpnReportsSummary().catch(() => null),
        dms.cpnListBatches().catch(() => ({ data: [] })),
        dms.cpnListBatches({ include_deleted: true }).catch(() => ({ data: [] })),
      ]);
      setSummary(s); setBatches(b.data || []); setDeletedBatches(db_.data || []);
      dms.cpnPrintHistory().then(h => setHistory(h.data || [])).catch(() => {});
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const t = summary?.totals || {};
  const cash = summary?.by_type?.cash || {};
  const reward = summary?.by_type?.reward || {};
  const cashValueUnused = cash?.unused?.value || 0;
  const rewardValueUnused = reward?.unused?.value || 0;

  // "Generated" (per spec) = TOTAL coupons ever generated across all statuses
  const totalGenerated = Object.values(t).reduce((a, b) => a + (Number(b) || 0), 0);
  // "Inactive" = created-but-not-usable (generated status + cancelled)
  const totalInactive  = (t.generated || 0) + (t.cancelled || 0) + (t.expired || 0);

  return (
    <div>
      <PageHeader
        title="Coupon Management"
        subtitle="Generate batches, activate, print sheets and monitor lifecycle end-to-end"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={load} disabled={busy} data-testid="cpn-refresh">
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
            </Button>
            <Button variant="outline" onClick={() => setSheetOpen(true)} data-testid="cpn-sheet-pdf-btn">
              <Printer size={16} className="mr-2" /> Sheet PDF
            </Button>
            <Button className={GOLD_BTN} onClick={() => setGenOpen(true)} data-testid="cpn-gen-btn">
              <Plus size={16} className="mr-2" /> Generate Batch
            </Button>
          </div>
        }
      />

      {/* Retailer self-scan permission toggle (Owner controls) */}
      <Card className="p-4 mb-5 flex items-center justify-between flex-wrap gap-3" data-testid="retailer-scan-perm-card">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2">
            <ScanLine size={16} className="text-[#a67c00]" /> Retailer Self-Scan Permission
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            When ON, retailers see a &quot;Scan Coupon&quot; option in their login and can scan coupons into their own wallet.
          </div>
        </div>
        <button
          onClick={toggleScanPerm}
          disabled={permBusy}
          data-testid="retailer-scan-toggle"
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${scanPerm ? "bg-emerald-500" : "bg-slate-300"}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${scanPerm ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Kpi label="Generated"      value={totalGenerated}               tint="bg-slate-100 text-slate-700"     icon={Ticket}
              onClick={() => nav("/dms/owner/coupons/all")}
              testid="kpi-generated" />
        <Kpi label="Inactive"       value={totalInactive}                tint="bg-slate-200 text-slate-700"     icon={PauseCircle}
              onClick={() => nav("/dms/owner/coupons/all?status=generated")}
              testid="kpi-inactive" />
        <Kpi label="Active"         value={t.unused || 0}                tint="bg-emerald-50 text-emerald-700"  icon={PackageCheck}
              onClick={() => nav("/dms/owner/coupons/all?status=unused")}
              testid="kpi-active" />
        <Kpi label="Claimed"        value={t.claimed || 0}               tint="bg-amber-50 text-amber-700"      icon={ScanLine}
              onClick={() => nav("/dms/owner/coupons/all?status=claimed")}
              testid="kpi-claimed" />
        <Kpi label="Redeemed"       value={t.redeemed || 0}              tint="bg-emerald-100 text-emerald-800" icon={CheckCircle2}
              onClick={() => nav("/dms/owner/coupons/all?status=redeemed")}
              testid="kpi-redeemed" />
        <Kpi label="Fraud Attempts" value={summary?.fraud_attempts || 0} tint="bg-rose-50 text-rose-700"        icon={ShieldAlert}
              onClick={() => nav("/dms/owner/coupons/fraud")}
              testid="kpi-fraud" />
      </div>

      {/* Type breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="text-emerald-700" size={18} />
            <h3 className="font-bold text-slate-900">Cash Coupons</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Unused" v={cash?.unused?.count} sub={inr(cash?.unused?.value)} />
            <Stat label="Claimed" v={cash?.claimed?.count} sub={inr(cash?.claimed?.value)} />
            <Stat label="Redeemed" v={cash?.redeemed?.count} sub={inr(cash?.redeemed?.value)} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="text-[#a67c00]" size={18} />
            <h3 className="font-bold text-slate-900">Reward Points Coupons</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Unused" v={reward?.unused?.count} sub={`${reward?.unused?.value || 0} pts`} />
            <Stat label="Claimed" v={reward?.claimed?.count} sub={`${reward?.claimed?.value || 0} pts`} />
            <Stat label="Redeemed" v={reward?.redeemed?.count} sub={`${reward?.redeemed?.value || 0} pts`} />
          </div>
        </Card>
      </div>

      {/* Batches Table */}
      <Card className="overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2">
          <Ticket size={16} /> Coupon Batches
          <span className="ml-auto text-xs text-slate-500">{batches.length} total</span>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Batch</TableHead><TableHead>Title</TableHead>
            <TableHead>Type</TableHead><TableHead className="text-right">Value</TableHead>
            <TableHead className="text-right">Count</TableHead>
            <TableHead>Status</TableHead><TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {batches.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">
                No batches yet — click Generate Batch to create your first
              </TableCell></TableRow>
            )}
            {batches.map(b => (
              <TableRow key={b.id} className="hover:bg-amber-50/30">
                <TableCell className="font-mono text-xs font-semibold">{b.batch_label}</TableCell>
                <TableCell>{b.title}</TableCell>
                <TableCell>
                  {b.coupon_type === "cash"
                    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-800"><Coins size={12} /> Cash</span>
                    : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800"><Award size={12} /> Reward</span>}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {b.coupon_type === "cash" ? inr(b.coupon_value) : `${b.coupon_value} pts`}
                </TableCell>
                <TableCell className="text-right">{b.count?.toLocaleString?.() || b.count}</TableCell>
                <TableCell><StatusChip s={b.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(b.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="mr-2"
                          disabled={pdfBusyId === b.id}
                          onClick={async () => {
                            setPdfBusyId(b.id);
                            try {
                              toast.info(`Generating 11×17 sheet PDF for ${b.count?.toLocaleString?.() || b.count} coupons (77 per sheet)… this can take a little while for large batches.`);
                              await dms.cpnExportPdfViaJob(b.id, `${b.batch_label}_sheet_35mm.pdf`, { side: "both" });
                              toast.success("Coupon PDF downloaded");
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "PDF failed"); }
                            finally { setPdfBusyId(null); }
                          }}
                          data-testid={`cpn-batch-sheet-${b.batch_no}`}>
                    {pdfBusyId === b.id
                      ? <><Loader2 size={14} className="mr-1 animate-spin" /> Generating…</>
                      : <><Printer size={14} className="mr-1" /> Generate PDF</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => nav(`/dms/owner/coupons/batches/${b.id}`)}
                          data-testid={`cpn-batch-view-${b.batch_no}`}>
                    Open <ArrowRight size={14} className="ml-1" />
                  </Button>
                  <Button size="sm" variant="outline" className="ml-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                          disabled={deleteBusyId === b.id}
                          onClick={async () => {
                            if (!window.confirm(`Delete batch ${b.batch_label} (${b.count?.toLocaleString?.() || b.count} coupons)? This can be restored within 30 days, after which it is permanently removed.`)) return;
                            setDeleteBusyId(b.id);
                            try {
                              const r = await dms.cpnDeleteBatch(b.id);
                              toast.success(`Batch deleted — restorable for 30 days (${r.coupons_affected || 0} coupons affected)`);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                            finally { setDeleteBusyId(null); }
                          }}
                          data-testid={`cpn-batch-delete-${b.batch_no}`}>
                    <Trash2 size={14} className="mr-1" /> Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {deletedBatches.length > 0 && (
        <Card className="overflow-x-auto mt-5 border-rose-100">
          <div className="px-4 py-3 border-b border-rose-100 font-semibold text-rose-900 flex items-center gap-2 bg-rose-50/50">
            <Trash2 size={16} /> Recently Deleted
            <span className="ml-auto text-xs text-rose-500">{deletedBatches.length} · restorable for 30 days</span>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Batch</TableHead><TableHead>Title</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead>Deleted On</TableHead><TableHead>Deleted By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {deletedBatches.map(b => (
                <TableRow key={b.id} className="hover:bg-rose-50/30">
                  <TableCell className="font-mono text-xs font-semibold">{b.batch_label}</TableCell>
                  <TableCell>{b.title}</TableCell>
                  <TableCell className="text-right">{b.count?.toLocaleString?.() || b.count}</TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(b.deleted_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.deleted_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={restoreBusyId === b.id}
                            onClick={async () => {
                              setRestoreBusyId(b.id);
                              try {
                                await dms.cpnRestoreBatch(b.id);
                                toast.success(`Batch ${b.batch_label} restored`);
                                load();
                              } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
                              finally { setRestoreBusyId(null); }
                            }}
                            data-testid={`cpn-batch-restore-${b.batch_no}`}>
                    {restoreBusyId === b.id
                      ? <><Loader2 size={14} className="mr-1 animate-spin" /> Restoring…</>
                      : <><RefreshCw size={14} className="mr-1" /> Restore</>}
                  </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <GenerateBatchDialog open={genOpen} onClose={() => setGenOpen(false)} onDone={(bid) => { load(); nav(`/dms/owner/coupons/batches/${bid}`); }} />
      <PrintSheetDialog open={sheetOpen} onClose={() => setSheetOpen(false)} batches={batches} onPrinted={load} />

      {/* Print History */}
      <Card className="mt-5 overflow-x-auto" data-testid="cpn-print-history">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2">
          <Clock size={16} /> Print History
          <span className="ml-auto text-xs text-slate-500">{history.length} print{history.length === 1 ? "" : "s"}</span>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Selection</TableHead>
            <TableHead className="text-right">Coupons</TableHead>
            <TableHead className="text-right">Sheets</TableHead>
            <TableHead>Printed</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {history.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">
                No prints yet — use “Sheet PDF” above to print coupons
              </TableCell></TableRow>
            )}
            {history.map(h => (
              <TableRow key={h.id} className="hover:bg-amber-50/30">
                <TableCell className="max-w-[360px]"><div className="text-sm text-slate-800 truncate" title={h.label}>{h.label}</div><div className="text-[11px] text-slate-400 uppercase">{h.side}</div></TableCell>
                <TableCell className="text-right font-semibold">{h.coupon_count?.toLocaleString?.() || h.coupon_count}</TableCell>
                <TableCell className="text-right">{h.sheet_count}</TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(h.created_at)}<div className="text-[11px] text-slate-400">{h.created_by_name}</div></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-2"
                    onClick={async () => {
                      try { toast.info("Preparing PDF…"); await dms.cpnPrintHistoryDownload(h.id, `GOOIL_reprint_${h.coupon_count}.pdf`); toast.success("Downloaded"); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
                    }}
                    data-testid={`print-hist-dl-${h.id}`}>
                    <Download size={14} className="mr-1" /> Download
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50"
                    onClick={async () => {
                      if (!window.confirm("Delete this print history record? (Coupons are NOT affected.)")) return;
                      try { await dms.cpnPrintHistoryDelete(h.id); toast.success("History deleted"); load(); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                    }}
                    data-testid={`print-hist-del-${h.id}`}>
                    <XCircle size={14} className="mr-1" /> Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═══════════ Print Sheet Dialog — multi-batch / serial-range printing ════════
// 77 coupons per 11x17in sheet. Auto sheet calculation. Saves to Print History.
function PrintSheetDialog({ open, onClose, batches, onPrinted }) {
  const printable = useMemo(
    () => (batches || []).filter(b => ["activated", "printed", "generated"].includes(b.status)),
    [batches]
  );
  const [mode, setMode] = useState("batch"); // "batch" | "range"
  const [selBatches, setSelBatches] = useState({}); // {batchId: true}
  const [rangeBatch, setRangeBatch] = useState("");
  const [fromSerial, setFromSerial] = useState("");
  const [toSerial, setToSerial] = useState("");
  const [side, setSide] = useState("both");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode("batch"); setSelBatches({}); setRangeBatch("");
      setFromSerial(""); setToSerial(""); setSide("both"); setPreview(null);
    }
  }, [open]);

  const buildBody = useCallback(() => {
    if (mode === "batch") {
      const ids = Object.keys(selBatches).filter(k => selBatches[k]);
      return ids.length ? { batch_ids: ids, side } : null;
    }
    if (!rangeBatch) return null;
    const item = { batch_id: rangeBatch };
    if (fromSerial.trim() && toSerial.trim()) {
      item.from_serial = fromSerial.trim();
      item.to_serial = toSerial.trim();
    }
    return { items: [item], side };
  }, [mode, selBatches, rangeBatch, fromSerial, toSerial, side]);

  // live preview (debounced)
  useEffect(() => {
    if (!open) return;
    const body = buildBody();
    if (!body) { setPreview(null); return; }
    let alive = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      dms.cpnPrintMixedPreview(body)
        .then(p => { if (alive) setPreview(p); })
        .catch(() => { if (alive) setPreview(null); })
        .finally(() => { if (alive) setPreviewing(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [open, buildBody]);

  const toggleBatch = (id) => setSelBatches(s => ({ ...s, [id]: !s[id] }));
  const selectedCount = Object.values(selBatches).filter(Boolean).length;

  const download = async () => {
    const body = buildBody();
    if (!body) { toast.error("Select at least one batch or a serial range"); return; }
    setDownloading(true);
    try {
      toast.info("Building 11×17 sheet (77 per sheet)…");
      await dms.cpnPrintMixed(body, `GOOIL_coupons_${preview?.coupon_count || ""}.pdf`);
      toast.success("Sheet PDF downloaded & saved to history");
      onPrinted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Printer size={18} /> Print Coupon Sheets</DialogTitle></DialogHeader>

        {/* Mode switch */}
        <div className="flex gap-2 mb-2">
          <button onClick={() => setMode("batch")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${mode === "batch" ? "border-[#c9a227] bg-[#faf6e6] text-[#8a6600]" : "border-slate-200 text-slate-600"}`}
            data-testid="print-mode-batch">
            By Batch
          </button>
          <button onClick={() => setMode("range")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${mode === "range" ? "border-[#c9a227] bg-[#faf6e6] text-[#8a6600]" : "border-slate-200 text-slate-600"}`}
            data-testid="print-mode-range">
            By Serial Range
          </button>
        </div>

        {mode === "batch" ? (
          <div className="space-y-2">
            <Label>Select one or more batches (mix allowed)</Label>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {printable.length === 0 && <div className="p-4 text-sm text-slate-400 text-center">No printable batches</div>}
              {printable.map(b => (
                <label key={b.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`print-pick-${b.batch_label}`}>
                  <input type="checkbox" checked={!!selBatches[b.id]} onChange={() => toggleBatch(b.id)} className="h-4 w-4 accent-[#a67c00]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{b.batch_label} · {b.title}</div>
                    <div className="text-[11px] text-slate-500">{b.coupon_type === "cash" ? inr(b.coupon_value) : `${b.coupon_value} pts`} · {b.count} coupons · {b.status}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="text-xs text-slate-500">{selectedCount} batch{selectedCount === 1 ? "" : "es"} selected</div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Batch</Label>
              <Select value={rangeBatch} onValueChange={setRangeBatch}>
                <SelectTrigger data-testid="print-range-batch"><SelectValue placeholder="Select a batch" /></SelectTrigger>
                <SelectContent>
                  {printable.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_label} · {b.title} ({b.count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From Serial</Label>
                <Input value={fromSerial} onChange={e => setFromSerial(e.target.value)} placeholder="e.g. ABC001" data-testid="print-from-serial" />
              </div>
              <div>
                <Label>To Serial</Label>
                <Input value={toSerial} onChange={e => setToSerial(e.target.value)} placeholder="e.g. ABC077" data-testid="print-to-serial" />
              </div>
            </div>
            <div className="text-[11px] text-slate-500">Leave From/To empty to print the entire batch.</div>
          </div>
        )}

        {/* Side selector */}
        <div className="flex items-center gap-2 mt-1">
          <Label className="mb-0">Sides:</Label>
          {["both", "front", "back"].map(s => (
            <button key={s} onClick={() => setSide(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize border ${side === s ? "border-[#c9a227] bg-[#faf6e6] text-[#8a6600]" : "border-slate-200 text-slate-500"}`}>
              {s}
            </button>
          ))}
        </div>

        {/* Live calculation */}
        <div className="mt-2 rounded-lg bg-[#faf6e6] border border-[#c9a227]/30 p-3" data-testid="print-preview">
          {previewing ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Calculating…</div>
          ) : preview && preview.coupon_count > 0 ? (
            <div>
              <div className="text-sm text-slate-800">
                <span className="font-bold">{preview.coupon_count.toLocaleString("en-IN")}</span> coupons →
                <span className="font-bold"> {preview.sheet_count}</span> sheet{preview.sheet_count === 1 ? "" : "s"}
                <span className="text-slate-500"> ({preview.per_sheet}/sheet)</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {preview.breakdown.map((n, i) => `Sheet ${i + 1} → ${n}`).join("  •  ")}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Select batches or a serial range to see the sheet calculation.</div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={GOLD_BTN} onClick={download} disabled={downloading || !preview || !preview.coupon_count} data-testid="print-download-btn">
            {downloading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Stat({ label, v, sub }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 border border-slate-100">
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-900">{(v || 0).toLocaleString("en-IN")}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═════════════════════════ OWNER: Generate Batch Dialog ═════════════════════
function GenerateBatchDialog({ open, onClose, onDone }) {
  const [type, setType] = useState("cash");
  const [value, setValue] = useState(20);
  const [count, setCount] = useState(100);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("prefix_sequential"); // or "random_secure"
  const [prefix, setPrefix] = useState("ABC");
  const [serialStart, setSerialStart] = useState(1);
  const [serialPad, setSerialPad] = useState(3);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setType("cash"); setValue(20); setCount(100); setTitle(""); setNotes("");
      setMode("prefix_sequential"); setPrefix("ABC");
      setSerialStart(1); setSerialPad(3);
    }
  }, [open]);

  // Auto-bump padding when serial count grows
  useEffect(() => {
    if (mode !== "prefix_sequential") return;
    const maxSerial = Number(serialStart) + Number(count) - 1;
    const needed = String(Math.max(1, maxSerial)).length;
    if (needed > Number(serialPad)) setSerialPad(needed);
  }, [count, serialStart, mode, serialPad]);

  const suggested = useMemo(() => {
    const unit = type === "cash" ? "₹" : "";
    const suffix = type === "cash" ? "" : " pts";
    if (mode === "prefix_sequential") {
      return `${type.toUpperCase()} ${prefix} × ${count}`;
    }
    return `${type.toUpperCase()} ${unit}${value}${suffix} × ${count}`;
  }, [type, value, count, mode, prefix]);

  const serialPreview = useMemo(() => {
    if (mode !== "prefix_sequential") return null;
    const pfx = (prefix || "").toUpperCase();
    const pad = Math.max(1, Number(serialPad) || 3);
    const start = Number(serialStart) || 1;
    const cnt = Math.max(1, Number(count) || 1);
    const first = `${pfx}${String(start).padStart(pad, "0")}`;
    const second = `${pfx}${String(start + 1).padStart(pad, "0")}`;
    const last = `${pfx}${String(start + cnt - 1).padStart(pad, "0")}`;
    return { first, second, last };
  }, [prefix, serialStart, serialPad, count, mode]);

  const submit = async () => {
    const v = Number(value);
    const c = Math.floor(Number(count));
    if (!v || v <= 0) { toast.error("Value must be > 0"); return; }
    if (!c || c <= 0 || c > 100000) { toast.error("Count must be 1 – 100,000"); return; }
    if (mode === "prefix_sequential") {
      const pfx = (prefix || "").trim().toUpperCase();
      if (!/^[A-Z0-9]{1,10}$/.test(pfx)) {
        toast.error("Prefix must be 1-10 chars: A-Z or 0-9 only");
        return;
      }
      const maxSerial = Number(serialStart) + c - 1;
      if (String(maxSerial).length > Number(serialPad)) {
        toast.error(`Padding ${serialPad} too small for serial ${maxSerial}`);
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        coupon_type: type, coupon_value: v, count: c,
        title: (title || suggested).trim(), notes: notes.trim(),
        serial_mode: mode,
      };
      if (mode === "prefix_sequential") {
        body.prefix = (prefix || "").toUpperCase();
        body.serial_start = Number(serialStart) || 1;
        body.serial_pad = Number(serialPad) || 3;
      }
      const r = await dms.cpnCreateBatch(body);
      toast.success(`Batch ${r.batch.batch_label} created — ${c.toLocaleString()} coupons`);
      onDone?.(r.batch.id); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate batch");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Generate Coupon Batch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Coupon Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="gen-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash (₹)</SelectItem>
                  <SelectItem value="reward">Reward Points</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{type === "cash" ? "Coupon Value (₹)" : "Points per Coupon"}</Label>
              <Input type="number" min={1} value={value} onChange={e => setValue(e.target.value)} data-testid="gen-value" />
            </div>
          </div>

          <div>
            <Label>Serial Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="gen-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prefix_sequential">Prefix + Sequential (e.g. ABC001, ABC002…)</SelectItem>
                <SelectItem value="random_secure">Random Secure (legacy, non-sequential)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "prefix_sequential" && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Prefix</Label>
                <Input value={prefix} maxLength={10}
                       onChange={e => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                       data-testid="gen-prefix" />
              </div>
              <div>
                <Label>Start #</Label>
                <Input type="number" min={0} value={serialStart}
                       onChange={e => setSerialStart(e.target.value)} data-testid="gen-start" />
              </div>
              <div>
                <Label>Padding</Label>
                <Input type="number" min={1} max={10} value={serialPad}
                       onChange={e => setSerialPad(e.target.value)} data-testid="gen-pad" />
              </div>
            </div>
          )}

          <div>
            <Label>Number of Coupons</Label>
            <Input type="number" min={1} max={100000} value={count} onChange={e => setCount(e.target.value)} data-testid="gen-count" />
            {mode === "prefix_sequential" && serialPreview ? (
              <p className="text-[11px] text-slate-600 mt-1">
                Preview: <span className="font-mono font-semibold text-slate-800">
                  {serialPreview.first}, {serialPreview.second}, … {serialPreview.last}
                </span>
              </p>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1">
                Codes will be non-sequential secure random (e.g. QSRD-9X7K-LA82-MPQ4). Max 100,000 per batch.
              </p>
            )}
          </div>

          <div>
            <Label>Title <span className="text-slate-400 text-xs">(optional)</span></Label>
            <Input value={title} placeholder={suggested} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 text-xs">(optional)</span></Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <b>Security:</b> Every coupon gets an independent UUID v4 hidden ID + AES-256 encrypted QR payload + HMAC-SHA256 signature.<br/>
            <b>Next steps:</b> Coupons start <b>inactive</b>. Activate a single coupon, a range, or the entire batch before printing.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className={GOLD_BTN} data-testid="gen-submit">
            {busy ? "Generating…" : "Generate Batch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════ OWNER: Batch Detail ══════════════════════════════
export function OwnerCouponBatchDetailPage() {
  const { bid } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [qrModal, setQrModal] = useState(null);   // { cid, serial } | null
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    if (!bid) return;
    setBusy(true);
    try {
      const [b, list] = await Promise.all([
        dms.cpnGetBatch(bid),
        dms.cpnListCoupons({ batch_id: bid, limit: 500, status: statusFilter || undefined }),
      ]);
      setBatch(b); setCoupons(list.data || []);
      setSelectedIds([]);   // clear selection on reload
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load batch");
    } finally { setBusy(false); }
  }, [bid, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!batch) {
    return <div className="p-10 text-center text-slate-400">{busy ? "Loading…" : "Batch not found"}</div>;
  }

  const cnt = batch.counts_by_status || {};
  const usable = ["generated"].includes(batch.status);
  const canActivate = batch.status === "generated";
  const canPrint = batch.active;

  return (
    <div>
      <PageHeader
        title={`Batch ${batch.batch_label}`}
        subtitle={
          <span className="flex items-center gap-2">
            {batch.title} <StatusChip s={batch.status} />
            {batch.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">ACTIVE</span>}
          </span>
        }
        action={
          <Button variant="outline" onClick={() => nav("/dms/owner/coupons")}>
            <ChevronLeft size={14} /> Back
          </Button>
        }
      />

      {/* Meta + counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Card className="p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Type</div>
          <div className="font-bold text-slate-900 flex items-center gap-2 mt-1">
            {batch.coupon_type === "cash"
              ? <><Coins size={16} className="text-emerald-700" /> Cash Coupon</>
              : <><Award size={16} className="text-[#a67c00]" /> Reward Points</>}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Value / Coupon</div>
          <div className="text-lg font-bold text-slate-900 mt-1">
            {batch.coupon_type === "cash" ? inr(batch.coupon_value) : `${batch.coupon_value} pts`}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Total Value</div>
          <div className="text-lg font-bold text-slate-900 mt-1">
            {batch.coupon_type === "cash"
              ? inr(batch.total_value)
              : `${(batch.total_value || 0).toLocaleString("en-IN")} pts`}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Total Coupons</div>
          <div className="text-lg font-bold text-slate-900 mt-1">
            {(batch.count || 0).toLocaleString("en-IN")}
          </div>
        </Card>
      </div>

      {/* Actions */}
      <Card className="p-4 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          {canActivate && (
            <Button className={GOLD_BTN} disabled={busy}
                    onClick={() => doAction(() => dms.cpnActivateBatch(bid), "Batch activated")}
                    data-testid="batch-activate">
              <Play size={14} className="mr-1" /> Activate Batch
            </Button>
          )}
          {canPrint && (
            <>
              <Button className={GOLD_BTN} disabled={busy}
                      onClick={() => setPrintOpen(true)}
                      data-testid="batch-pdf">
                <Printer size={14} className="mr-1" /> Print &amp; Share
              </Button>
              <Button variant="outline" disabled={busy}
                      onClick={() => dms.cpnExportXlsx(bid, `${batch.batch_label}_manifest.xlsx`).catch(e => toast.error("Failed"))}
                      data-testid="batch-xlsx">
                <FileSpreadsheet size={14} className="mr-1" /> Export Excel (Audit)
              </Button>
              <Button variant="outline" disabled={busy}
                      onClick={() => doAction(() => dms.cpnMarkPrinted(bid), "Marked as printed")}>
                Mark Printed
              </Button>
              <Button variant="outline" disabled={busy}
                      onClick={() => doAction(() => dms.cpnIssueToProd(bid), "Issued to production")}>
                <Truck size={14} className="mr-1" /> Issue to Production
              </Button>
            </>
          )}
          {batch.active && (
            <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Deactivate this batch? All unused coupons will be cancelled.")) return;
                      doAction(() => dms.cpnDeactivateBatch(bid), "Batch deactivated");
                    }}>
              Deactivate
            </Button>
          )}
        </div>
      </Card>

      {/* Coupon lifecycle counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <Kpi label="Generated" value={cnt.generated || 0} />
        <Kpi label="Unused" value={cnt.unused || 0} tint="bg-emerald-50 text-emerald-700" icon={PackageCheck} />
        <Kpi label="Claimed" value={cnt.claimed || 0} tint="bg-amber-50 text-amber-700" icon={ScanLine} />
        <Kpi label="Redeem Pending" value={cnt.redemption_pending || 0} tint="bg-orange-50 text-orange-700" icon={Activity} />
        <Kpi label="Redeemed" value={cnt.redeemed || 0} tint="bg-emerald-100 text-emerald-800" icon={CheckCircle2} />
        <Kpi label="Expired" value={cnt.expired || 0} tint="bg-slate-200 text-slate-700" />
        <Kpi label="Cancelled" value={cnt.cancelled || 0} tint="bg-rose-50 text-rose-700" />
      </div>

      {/* Coupons list */}
      <Card className="overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">Coupons in this batch</span>
          {selectedIds.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
              {selectedIds.length} selected
            </span>
          )}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Select value={statusFilter || "__all__"} onValueChange={v => setStatusFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {["generated", "unused", "claimed", "redemption_pending", "redeemed", "expired", "cancelled"]
                  .map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedIds.length > 0 && (
              <>
                <Button variant="outline" size="sm" disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const r = await dms.cpnBulkActivate(selectedIds);
                            toast.success(`Activated ${r.activated} of ${r.requested} coupons`);
                            await load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          finally { setBusy(false); }
                        }}
                        data-testid="bulk-activate">
                  <Play size={14} className="mr-1" /> Activate Selected
                </Button>
                <Button variant="outline" size="sm" disabled={busy}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const r = await dms.cpnBulkDeactivate(selectedIds);
                            toast.success(`Deactivated ${r.deactivated} of ${r.requested} coupons`);
                            await load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          finally { setBusy(false); }
                        }}
                        data-testid="bulk-deactivate">
                  Deactivate Selected
                </Button>
                <Button variant="outline" size="sm" disabled={busy}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={async () => {
                          const reason = window.prompt(`Reason to void ${selectedIds.length} coupon(s)? (already-claimed/redeemed coupons will be skipped)`);
                          if (!reason || !reason.trim()) return;
                          setBusy(true);
                          try {
                            const r = await dms.cpnBulkVoid(selectedIds, reason.trim());
                            toast.success(`Voided ${r.voided} of ${r.requested} coupons`);
                            setSelectedIds([]);
                            await load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          finally { setBusy(false); }
                        }}
                        data-testid="bulk-void">
                  Void Selected
                </Button>
              </>
            )}
            {batch.serial_mode === "prefix_sequential" && (
              <Button variant="outline" size="sm"
                      onClick={() => setRangeOpen(true)} data-testid="open-range-dialog">
                <Play size={14} className="mr-1" /> Activate by Range
              </Button>
            )}
          </div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8">
              <input type="checkbox"
                     className="h-4 w-4 accent-amber-600"
                     data-testid="select-all"
                     checked={coupons.length > 0 && selectedIds.length === coupons.length}
                     onChange={e => setSelectedIds(e.target.checked ? coupons.map(c => c.id) : [])} />
            </TableHead>
            <TableHead>Visible Serial</TableHead>
            <TableHead>Unique ID (hidden)</TableHead>
            <TableHead>QR</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Active</TableHead>
            <TableHead>Retailer</TableHead>
            <TableHead>Claimed On</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {coupons.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">
                No coupons match this filter
              </TableCell></TableRow>
            )}
            {coupons.map(c => {
              const canAct = ["generated", "unused"].includes(c.status) && !c.active;
              const canDeact = ["generated", "unused"].includes(c.status) && c.active;
              const serial = c.visible_serial || c.coupon_code;
              const uid = c.hidden_secure_id || "—";
              return (
                <TableRow key={c.id} className={selectedIds.includes(c.id) ? "bg-amber-50/60" : ""}>
                  <TableCell>
                    <input type="checkbox"
                           className="h-4 w-4 accent-amber-600"
                           data-testid={`select-${c.id}`}
                           checked={selectedIds.includes(c.id)}
                           onChange={e => {
                             setSelectedIds(prev => e.target.checked
                               ? [...prev, c.id]
                               : prev.filter(x => x !== c.id));
                           }} />
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-xs">{serial}</TableCell>
                  <TableCell className="text-[10px] font-mono text-slate-500">
                    {uid !== "—" ? (
                      <button className="hover:text-slate-800 hover:underline decoration-dotted"
                              title={`Click to copy: ${uid}`}
                              onClick={() => {
                                navigator.clipboard?.writeText(uid);
                                toast.success("Unique ID copied");
                              }}>
                        {uid.slice(0, 8)}…{uid.slice(-4)}
                      </button>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <button
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1"
                      onClick={() => setQrModal({ cid: c.id, serial })}
                      data-testid={`qr-${c.id}`}>
                      <ScanLine size={13} /> View
                    </button>
                  </TableCell>
                  <TableCell><StatusChip s={c.status} /></TableCell>
                  <TableCell>
                    {c.active
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">ACTIVE</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">INACTIVE</span>}
                  </TableCell>
                  <TableCell className="text-xs">{c.retailer_name || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500">{c.claim_timestamp ? niceDate(c.claim_timestamp) : "—"}</TableCell>
                  <TableCell className="text-right">
                    {canAct && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                              onClick={() => doAction(() => dms.cpnActivateCoupon(c.id), "Coupon activated")}
                              data-testid={`activate-${c.id}`}>
                        Activate
                      </Button>
                    )}
                    {canDeact && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busy}
                              onClick={() => doAction(() => dms.cpnDeactivateCoupon(c.id), "Coupon deactivated")}>
                        Deactivate
                      </Button>
                    )}
                    {!["claimed", "redemption_pending", "redeemed", "voided"].includes(c.status) && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 ml-1" disabled={busy}
                              onClick={() => {
                                const reason = window.prompt(`Reason to void coupon ${serial}?`);
                                if (!reason || !reason.trim()) return;
                                doAction(() => dms.cpnVoidSerial(serial, reason.trim()), "Coupon voided");
                              }}
                              data-testid={`void-${c.id}`}>
                        Void
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Activate Range dialog */}
      <ActivateRangeDialog open={rangeOpen} onClose={() => setRangeOpen(false)}
                            batch={batch} onDone={load} />

      {/* Print & Share dialog */}
      <PrintShareDialog open={printOpen} onClose={() => setPrintOpen(false)}
                        batch={batch} onDone={load} />

      {/* QR view modal */}
      <CouponQrModal modal={qrModal} onClose={() => setQrModal(null)} />
    </div>
  );
}

function PrintShareDialog({ open, onClose, batch, onDone }) {
  const [diameter, setDiameter] = useState(34);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null);      // { share_url, expires_at }
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (open) {
      setShare(null);
      setPhone("");
      setDiameter(34);
    }
  }, [open]);

  const gridEstimate = useMemo(() => {
    // Rough A4 fit estimate (matches backend calc): 210×297mm usable ≈ (210-16)/((d+2))
    const cols = Math.max(1, Math.floor((210 - 16 + 2) / (diameter + 2)));
    const rows = Math.max(1, Math.floor((297 - 16 + 2) / (diameter + 2)));
    return { cols, rows, per_page: cols * rows };
  }, [diameter]);

  if (!batch) return null;

  const doDownload = async () => {
    setBusy(true);
    try {
      // Large batches (1000s of coupons / many sheets) could exceed the
      // proxy's 30s timeout on the old synchronous endpoint, which showed up
      // as a blank "504 Gateway Time-out" page instead of a PDF. The
      // background job (render -> poll -> download) handles big batches
      // reliably instead.
      await dms.cpnExportPdfViaJob(batch.id, `${batch.batch_label}_coupons_35mm.pdf`);
      toast.success("PDF downloaded — send it to your printer");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Download failed");
    } finally { setBusy(false); }
  };

  const doCreateShareLink = async () => {
    setBusy(true);
    try {
      const r = await dms.cpnCreateShareLink(batch.id, { diameter_mm: diameter });
      setShare(r);
      toast.success("Share link ready — valid 24 hours");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const openWhatsApp = async () => {
    if (!share) return;
    const text =
      `📦 *GO OIL Coupon Print Job*\n\n` +
      `*Batch:* ${share.batch_label}\n` +
      `*Coupons:* ${share.coupon_count}\n` +
      `*Size:* ${share.diameter_mm} mm (circular die-cut)\n\n` +
      `📄 *Download PDF:* ${share.share_url}\n\n` +
      `_Please print full colour on 300 gsm art paper. Die-cut along red dashed circles. QR must be sharp and scannable._\n\n` +
      `_Link valid till ${new Date(share.expires_at).toLocaleString("en-IN")}_`;

    // Prefer native Web Share API (mobile) — auto-attaches PDF where supported
    if (navigator.share) {
      try {
        // Try file share first
        const blob = await dms.cpnExportPdfBlob(batch.id, { diameter_mm: diameter });
        const file = new File([blob], `${batch.batch_label}_coupons_${diameter}mm.pdf`,
                              { type: "application/pdf" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `GO OIL Coupons – ${share.batch_label}`,
            text,
            files: [file],
          });
          return;
        }
        // Fallback: share text/link only
        await navigator.share({ title: `GO OIL Coupons – ${share.batch_label}`, text });
        return;
      } catch (_e) { /* user cancelled or failed → fall through to wa.me */ }
    }
    // Fallback: wa.me deep link
    const cleanPhone = (phone || "").replace(/[^0-9]/g, "");
    const url = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  const copyLink = () => {
    if (!share) return;
    navigator.clipboard?.writeText(share.share_url);
    toast.success("Link copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-32px)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={18} className="text-[#a67c00]" />
            Print &amp; Share to Printer
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Batch context */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-sm flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="text-slate-500">Batch:</span>{" "}
              <span className="font-mono font-semibold">{batch.batch_label}</span></span>
            <span><span className="text-slate-500">Coupons:</span>{" "}
              <span className="font-semibold">{batch.count}</span></span>
            <span><span className="text-slate-500">Type:</span>{" "}
              <span className="font-semibold">
                {batch.coupon_type === "cash" ? "Cash Coupon" : "Reward Coupon"}
              </span></span>
            <span><span className="text-slate-500">Value:</span>{" "}
              <span className="font-semibold text-[#8a6600]">
                {batch.coupon_type === "cash" ? inr(batch.coupon_value) : `${batch.coupon_value} pts`}
              </span></span>
          </div>

          {/* Diameter selector */}
          <div>
            <Label className="flex items-center justify-between mb-1.5">
              <span>Coupon Size (Diameter)</span>
              <span className="text-lg font-black text-[#8a6600]">{diameter} mm</span>
            </Label>
            <input type="range" min="20" max="80" step="1"
                    value={diameter}
                    onChange={e => setDiameter(Number(e.target.value))}
                    className="w-full accent-[#a67c00]"
                    disabled={busy}
                    data-testid="print-size-slider" />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>20mm</span><span>34mm (CorelDraw)</span><span>80mm</span>
            </div>
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {[25, 30, 34, 40, 50, 60].map(d => (
                <button key={d} type="button"
                        onClick={() => setDiameter(d)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${
                          diameter === d
                            ? "bg-[#8a6600] text-white border-[#8a6600]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-amber-300"
                        }`}>
                  {d}mm{d === 34 ? " ★" : ""}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              Estimated layout: <b>{gridEstimate.cols}×{gridEstimate.rows}</b> ={" "}
              <b>{gridEstimate.per_page} coupons/A4 page</b> ≈{" "}
              <b>{Math.ceil((batch.count || 0) / gridEstimate.per_page)} pages</b> total
            </div>
          </div>

          {/* Section 1: Direct download */}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold mb-2 flex items-center gap-1.5">
              <Download size={12} /> Option 1 — Download &amp; forward manually
            </div>
            <Button onClick={doDownload} disabled={busy} className={GOLD_BTN + " w-full"}
                    data-testid="print-download">
              <Download size={14} className="mr-1" />
              Download PDF ({diameter}mm)
            </Button>
          </div>

          {/* Section 2: WhatsApp share */}
          <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/40 p-3">
            <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-2 flex items-center gap-1.5">
              <MessageCircle size={12} /> Option 2 — Send directly to printer on WhatsApp
            </div>
            {!share ? (
              <Button onClick={doCreateShareLink} disabled={busy}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="print-create-link">
                <Link2 size={14} className="mr-1" />
                Generate WhatsApp Share Link (valid 24 h)
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-[10px] text-slate-600 leading-relaxed">
                  Public link ready. Expires{" "}
                  <b>{new Date(share.expires_at).toLocaleString("en-IN")}</b>.
                </div>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 text-[10px] bg-white border border-slate-200 rounded px-2 py-1.5 font-mono truncate">
                    {share.share_url}
                  </code>
                  <button onClick={copyLink}
                          className="text-[10px] px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 shrink-0"
                          title="Copy link">
                    <Copy size={11} />
                  </button>
                </div>
                <div>
                  <Label className="text-[11px]">Printer&apos;s WhatsApp Number (optional)</Label>
                  <Input value={phone}
                          onChange={e => setPhone(e.target.value)}
                          placeholder="e.g. 91XXXXXXXXXX (with country code)"
                          className="h-9 text-sm"
                          data-testid="print-phone" />
                </div>
                <Button onClick={openWhatsApp} disabled={busy}
                        className="w-full bg-[#25d366] hover:bg-[#128c7e] text-white"
                        data-testid="print-open-wa">
                  <MessageCircle size={14} className="mr-1" />
                  {phone ? "Send to Printer on WhatsApp" : "Open WhatsApp Share"}
                </Button>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  On mobile the WhatsApp app opens with a ready message including
                  the PDF link. On desktop, WhatsApp Web opens. Attach the PDF
                  from the copied link.
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CouponQrModal({ modal, onClose }) {
  const [data, setData] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showInternal, setShowInternal] = useState(false);

  useEffect(() => {
    if (!modal) { setData(null); setImgUrl(null); setShowInternal(false); return; }
    let alive = true;
    setBusy(true);
    (async () => {
      try {
        const [d, url] = await Promise.all([
          dms.cpnCouponQrPayload(modal.cid),
          dms.cpnCouponQrImageBlob(modal.cid, 8),
        ]);
        if (alive) { setData(d); setImgUrl(url); }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load QR");
        if (alive) onClose();
      } finally { if (alive) setBusy(false); }
    })();
    return () => {
      alive = false;
      if (imgUrl) URL.revokeObjectURL(imgUrl);
    };
  }, [modal]);

  if (!modal) return null;

  const valueLabel = data
    ? (data.coupon_type === "cash" ? `₹${data.coupon_value}` : `${data.coupon_value} pts`)
    : "";

  return (
    <Dialog open={!!modal} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-32px)] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine size={18} className="text-[#a67c00]" />
            <span className="font-mono">{modal.serial}</span>
          </DialogTitle>
        </DialogHeader>

        {busy && !data && (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-500 text-sm">
            <RefreshCw size={20} className="animate-spin" />
            Generating QR…
          </div>
        )}

        {data && (
          <div className="space-y-3">
            {/* ─── QR card (looks like a mini-coupon) ───────────────────── */}
            <div className="mx-auto rounded-2xl border-2 border-[#e6d194] bg-gradient-to-b from-[#0d0d0d] to-[#1a1a1a] p-4 flex flex-col items-center gap-2 w-full max-w-[280px]">
              <div className="text-[10px] uppercase tracking-widest text-[#f5c542] font-bold">
                GO OIL · Mechanic Coupon
              </div>
              {imgUrl ? (
                <div className="rounded-lg bg-white p-2 shadow">
                  <img src={imgUrl} alt="Coupon QR"
                       className="block"
                       style={{ width: 220, height: 220, imageRendering: "pixelated" }} />
                </div>
              ) : (
                <div className="w-[220px] h-[220px] bg-white/10 animate-pulse rounded-lg" />
              )}
              <div className="rounded-full bg-[#f5c542] px-3 py-0.5 text-[11px] font-black text-black tracking-wide">
                {data.coupon_type === "cash" ? `CASH  ${valueLabel}` : `REWARD  ${valueLabel}`}
              </div>
              <div className="font-mono text-[11px] text-[#f5c542] font-bold">
                {data.visible_serial}
              </div>
            </div>

            {/* ─── Status pill row ─────────────────────────────────────── */}
            <div className="flex items-center justify-center gap-2 text-xs">
              <StatusChip s={data.status} />
              {data.active
                ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">ACTIVE</span>
                : <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold">INACTIVE</span>}
            </div>

            {/* ─── Public info (always visible) ────────────────────────── */}
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              <MiniRow k="Serial" v={<span className="font-mono">{data.visible_serial}</span>} />
              <MiniRow k="Type"   v={<span className="font-semibold">{data.coupon_type === "cash" ? "Cash Coupon" : "Reward Coupon"}</span>} />
              <MiniRow k="Value"  v={<span className="font-semibold text-[#8a6600]">{valueLabel}</span>} />
            </div>

            {/* ─── Owner-only sensitive info — collapsed by default ────── */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowInternal(v => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100/60 transition">
                <Lock size={12} />
                <span className="flex-1 text-left">Internal Security Info (Owner Only)</span>
                {showInternal ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              {showInternal && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-amber-200">
                  <MiniCode label="Hidden Secure ID (UUID v4)" value={data.hidden_secure_id || "—"} />
                  <MiniCode label={`Encrypted QR Payload (${data.qr_version || "v2"})`}
                            value={data.qr_payload} truncate />
                  <p className="text-[10px] text-amber-900/80 leading-relaxed">
                    AES-256-GCM encrypted + HMAC-SHA256 signed. Never appears on printed coupons.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {imgUrl && data && (
            <Button
              variant="outline"
              onClick={() => {
                const a = document.createElement("a");
                a.href = imgUrl;
                a.download = `${data.visible_serial}_qr.png`;
                a.click();
              }}>
              <Download size={14} className="mr-1" /> Download QR
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniRow({ k, v }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 text-xs">
      <span className="text-slate-500">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function MiniCode({ label, value, truncate = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-amber-900 font-bold tracking-wide mb-0.5">{label}</div>
      <div className="flex items-center gap-2">
        <code className={`flex-1 text-[10px] bg-white border border-amber-200 rounded px-2 py-1 font-mono ${truncate ? "truncate" : "break-all"}`}>
          {value}
        </code>
        <button className="text-[10px] px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-50 text-amber-900 font-semibold shrink-0"
                onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }}>
          <Copy size={11} />
        </button>
      </div>
    </div>
  );
}

function FieldCopy({ label, value, mono = false, truncate = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">{label}</div>
      <div className="flex items-center gap-2">
        <code className={`flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 ${mono ? "font-mono" : ""} ${truncate ? "truncate" : "break-all"}`}>
          {value}
        </code>
        <button className="text-[10px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-600"
                onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }}>
          Copy
        </button>
      </div>
    </div>
  );
}

function ActivateRangeDialog({ open, onClose, batch, onDone }) {
  const [mode, setMode] = useState("serial");     // "serial" or "number"
  const [fromS, setFromS] = useState("");
  const [toS, setToS] = useState("");
  const [fromN, setFromN] = useState("");
  const [toN, setToN] = useState("");
  const [busy, setBusy] = useState(false);        // preview-in-flight OR activating
  const [preview, setPreview] = useState(null);   // {coupons_found, already_active, ready_to_activate, skipped, ...}
  const [previewError, setPreviewError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const previewTimer = useRef(null);

  useEffect(() => {
    if (open && batch) {
      const pad = batch.serial_pad || 3;
      const p = batch.prefix || "";
      const start = batch.serial_start ?? 1;
      const end = batch.serial_end ?? start;
      setFromS(`${p}${String(start).padStart(pad, "0")}`);
      setToS(`${p}${String(end).padStart(pad, "0")}`);
      setFromN(start);
      setToN(end);
      setMode("serial");
      setPreview(null);
      setPreviewError("");
    }
  }, [open, batch]);

  // Local prediction (only for showing the normalized "will activate" range)
  const localRange = useMemo(() => {
    if (!batch?.prefix) return null;
    const pad = batch.serial_pad || 3;
    const norm = (v) => {
      const s = String(v || "").trim().toUpperCase();
      if (!s) return "";
      if (s.startsWith(batch.prefix)) {
        const num = s.slice(batch.prefix.length).replace(/^0+/, "") || "0";
        return /^\d+$/.test(num) ? `${batch.prefix}${num.padStart(pad, "0")}` : s;
      }
      if (/^\d+$/.test(s)) return `${batch.prefix}${s.padStart(pad, "0")}`;
      return s;
    };
    if (mode === "serial") return { fs: norm(fromS), ts: norm(toS) };
    return {
      fs: `${batch.prefix}${String(fromN || 0).padStart(pad, "0")}`,
      ts: `${batch.prefix}${String(toN || 0).padStart(pad, "0")}`,
    };
  }, [batch, fromS, toS, fromN, toN, mode]);

  // Debounced live preview — hit /activate-range/preview whenever range changes
  useEffect(() => {
    if (!open || !batch) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      const body = { batch_id: batch.id };
      if (mode === "serial") {
        if (!fromS || !toS) { setPreview(null); setPreviewError(""); return; }
        body.from_serial = fromS; body.to_serial = toS;
      } else {
        const f = Number(fromN), t = Number(toN);
        if (!f || !t) { setPreview(null); setPreviewError(""); return; }
        body.from_number = f; body.to_number = t;
      }
      setBusy(true);
      try {
        const r = await dms.cpnActivateRangePreview(body);
        setPreview(r);
        setPreviewError("");
      } catch (e) {
        setPreview(null);
        setPreviewError(e?.response?.data?.detail || "Could not compute preview");
      } finally { setBusy(false); }
    }, 350);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [open, batch, mode, fromS, toS, fromN, toN]);

  const doActivate = async () => {
    if (!preview || !preview.ready_to_activate) return;
    setBusy(true);
    try {
      const body = { batch_id: batch.id };
      if (mode === "serial") { body.from_serial = fromS; body.to_serial = toS; }
      else { body.from_number = Number(fromN); body.to_number = Number(toN); }
      const r = await dms.cpnActivateRange(body);
      toast.success(`Activated ${r.activated} of ${r.matched} in range ${r.from_serial} – ${r.to_serial}`);
      setConfirmOpen(false);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate range");
    } finally { setBusy(false); }
  };

  const canActivate = !!preview && preview.ready_to_activate > 0 && !previewError;
  const typeLabel = (batch?.coupon_type === "cash")
    ? `Cash Coupon` : (batch?.coupon_type === "reward" ? `Reward Coupon` : "");
  const valueLabel = batch
    ? (batch.coupon_type === "cash" ? inr(batch.coupon_value) : `${batch.coupon_value} Points`)
    : "";

  return (
    <>
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Activate Coupons</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Batch header context (Coupon Type & Value are BATCH-level, not user-input) */}
          {batch && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-500">Batch:</span>
              <span className="font-mono font-semibold text-slate-900">{batch.batch_label}</span>
              <span className="text-slate-500 ml-2">Type:</span>
              <span className="font-semibold text-slate-900">{typeLabel}</span>
              <span className="text-slate-500 ml-2">Value:</span>
              <span className="font-semibold text-[#8a6600]">{valueLabel}</span>
            </div>
          )}
          <div>
            <Label>Input Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="serial">By Serial (e.g. {batch?.prefix || "ABC"}1 to {batch?.prefix || "ABC"}100)</SelectItem>
                <SelectItem value="number">By Number Only (1 to 100)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "serial" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From Serial Number</Label>
                <Input value={fromS} onChange={e => setFromS(e.target.value.toUpperCase())}
                       placeholder={`${batch?.prefix || "ABC"}001`} data-testid="range-from-serial" />
              </div>
              <div>
                <Label>To Serial Number</Label>
                <Input value={toS} onChange={e => setToS(e.target.value.toUpperCase())}
                       placeholder={`${batch?.prefix || "ABC"}100`} data-testid="range-to-serial" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From #</Label>
                <Input type="number" value={fromN} onChange={e => setFromN(e.target.value)} data-testid="range-from" />
              </div>
              <div>
                <Label>To #</Label>
                <Input type="number" value={toN} onChange={e => setToN(e.target.value)} data-testid="range-to" />
              </div>
            </div>
          )}

          {/* Normalized range hint */}
          {localRange && localRange.fs && localRange.ts && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono">
              Range: <b>{localRange.fs}</b> … <b>{localRange.ts}</b>
            </div>
          )}

          {/* ── LIVE PREVIEW panel ─────────────────────────────────────── */}
          <div className="rounded-lg border-2 border-[#e6d194] bg-gradient-to-br from-amber-50 to-white p-3">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={14} className="text-[#a67c00]" />
              <div className="text-xs font-bold uppercase tracking-wider text-[#8a6600]">
                Live Preview
              </div>
              {busy && (
                <RefreshCw size={12} className="animate-spin text-slate-400 ml-auto" />
              )}
            </div>
            {previewError ? (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{previewError}</span>
              </div>
            ) : preview ? (
              <div className="grid grid-cols-4 gap-2 text-center">
                <PreviewStat label="Coupons Found"       value={preview.coupons_found}      tint="text-slate-800" />
                <PreviewStat label="Already Active"      value={preview.already_active}     tint="text-blue-700" />
                <PreviewStat label="Ready to Activate"   value={preview.ready_to_activate}  tint="text-emerald-700" />
                <PreviewStat label="Skipped"             value={preview.skipped}            tint="text-slate-500" />
              </div>
            ) : (
              <div className="text-xs text-slate-500 text-center py-2">
                Enter a range to see the preview…
              </div>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-[11px] text-slate-600 leading-relaxed">
            <b>Skipped</b> = coupons that cannot be activated (already claimed, redeemed, cancelled or expired).
            Coupon <b>Type</b> and <b>Value</b> come from the batch and cannot be changed here.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canActivate || busy}
            className={GOLD_BTN}
            data-testid="range-submit">
            <Play size={14} className="mr-1" />
            {preview && preview.ready_to_activate
              ? `Activate ${preview.ready_to_activate.toLocaleString("en-IN")} Coupon${preview.ready_to_activate === 1 ? "" : "s"}`
              : "Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Confirmation dialog — asks final Yes/Cancel */}
    <Dialog open={confirmOpen} onOpenChange={o => !o && setConfirmOpen(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Activation</DialogTitle>
        </DialogHeader>
        {preview && (
          <div className="space-y-3">
            <div className="text-center py-3">
              <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">You are about to activate</div>
              <div className="text-4xl font-black text-[#8a6600] my-1">
                {preview.ready_to_activate.toLocaleString("en-IN")}
              </div>
              <div className="text-sm text-slate-600">
                Coupon{preview.ready_to_activate === 1 ? "" : "s"}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
              <ConfirmRow k="Range" v={<span className="font-mono font-semibold">{preview.from_serial} → {preview.to_serial}</span>} />
              <ConfirmRow k="Coupon Type" v={<span className="font-semibold">{typeLabel}</span>} />
              <ConfirmRow k="Value" v={<span className="font-semibold text-[#8a6600]">{valueLabel}</span>} />
              <ConfirmRow k="Batch" v={<span className="font-mono">{preview.batch_label}</span>} />
              {preview.already_active > 0 && (
                <ConfirmRow k="Already Active (skipped)" v={<span className="text-blue-700">{preview.already_active}</span>} />
              )}
              {preview.skipped > 0 && (
                <ConfirmRow k="Skipped (claimed / cancelled)" v={<span className="text-slate-500">{preview.skipped}</span>} />
              )}
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Only <b>{preview.ready_to_activate}</b> coupon{preview.ready_to_activate === 1 ? "" : "s"} will be activated.
              An audit-log entry will be created with your name, timestamp, range and count.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={doActivate} disabled={busy} className={GOLD_BTN} data-testid="confirm-activate">
            {busy ? "Activating…" : "Yes, Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function PreviewStat({ label, value, tint = "text-slate-800" }) {
  return (
    <div className="rounded bg-white/80 border border-slate-200 px-2 py-2">
      <div className={`text-2xl font-black ${tint}`}>
        {(value || 0).toLocaleString("en-IN")}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-0.5">
        {label}
      </div>
    </div>
  );
}

function ConfirmRow({ k, v }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-slate-500">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function PreviewKV({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-1">
      <span className="text-slate-500 text-xs">{k}</span>
      <span className="font-semibold text-slate-900 text-right">{v ?? "—"}</span>
    </div>
  );
}


// ═════════════════════════ OWNER: All Coupons list (across batches) ═════════
export function OwnerCouponsListPage() {
  // Pre-populate filters from URL query string (?status=unused&type=cash)
  const searchParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await dms.cpnListCoupons({
        status: status || undefined,
        coupon_type: type || undefined,
        code: q.trim() ? q.trim().toUpperCase() : undefined,
        limit: 500,
      });
      setRows(r.data || []);
    } finally { setBusy(false); }
  }, [status, type, q]);

  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);

  return (
    <div>
      <PageHeader title="All Coupons" subtitle="Search across all batches — filter by type, status or exact code" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label>Type</Label>
          <Select value={type || "__all__"} onValueChange={v => setType(v === "__all__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="reward">Reward</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status || "__all__"} onValueChange={v => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {["generated", "unused", "claimed", "redemption_pending", "redeemed", "expired", "cancelled"]
                .map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Coupon Code (exact)</Label>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. QSRD-9X7K-LA82-MPQ4" />
        </div>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Batch</TableHead>
            <TableHead>Type</TableHead><TableHead className="text-right">Value</TableHead>
            <TableHead>Status</TableHead><TableHead>Retailer</TableHead>
            <TableHead>Distributor</TableHead><TableHead>Claimed On</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">
                {busy ? "Loading…" : "No coupons match"}
              </TableCell></TableRow>
            )}
            {rows.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs font-semibold">{c.coupon_code}</TableCell>
                <TableCell className="text-xs">{c.batch_label}</TableCell>
                <TableCell><StatusChip s={c.coupon_type} /></TableCell>
                <TableCell className="text-right">
                  {c.coupon_type === "cash" ? inr(c.coupon_value) : `${c.coupon_value} pts`}
                </TableCell>
                <TableCell><StatusChip s={c.status} /></TableCell>
                <TableCell className="text-xs">{c.retailer_name || "—"}</TableCell>
                <TableCell className="text-xs">{c.distributor_name || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{c.claim_timestamp ? niceDate(c.claim_timestamp) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═════════════════════════ OWNER / ACCOUNTANT: Redemptions ══════════════════
export function OwnerRedemptionsPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [wtype, setWtype] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await dms.cpnListRedemptions({
        status: status || undefined,
        wallet_type: wtype || undefined,
      });
      setRows(r.data || []);
    } finally { setBusy(false); }
  }, [status, wtype]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(
      r.wallet_type === "cash"
        ? `Approve ${r.redemption_no}? A Credit Note will be issued for ${inr(r.amount)} and the distributor's outstanding will reduce.`
        : `Approve ${r.redemption_no}? A Dispatch Advice will be issued for ${r.amount} points.`,
    )) return;
    try { await dms.cpnApproveRedemption(r.id); toast.success("Redemption approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reject = async (r) => {
    const reason = window.prompt("Reason for rejection:", "");
    if (reason == null) return;
    try { await dms.cpnRejectRedemption(r.id, reason); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Redemption Requests"
        subtitle="Approve cash → Credit Note, or points → Dispatch Advice. Distributor outstanding reduces only after approval."
        action={
          <Button className={GOLD_BTN} onClick={() => setNewOpen(true)} data-testid="new-red-btn">
            <Plus size={14} className="mr-1" /> New Redemption
          </Button>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label>Status</Label>
          <Select value={status || "__all__"} onValueChange={v => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Type</Label>
          <Select value={wtype || "__all__"} onValueChange={v => setWtype(v === "__all__" ? "" : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="reward">Reward</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Redemption</TableHead><TableHead>Retailer</TableHead>
            <TableHead>Distributor</TableHead><TableHead>Type</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead><TableHead>Requested</TableHead>
            <TableHead>CN / DA</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">No requests</TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs font-semibold">{r.redemption_no}</TableCell>
                <TableCell>{r.retailer_name}</TableCell>
                <TableCell className="text-xs">{r.distributor_name}</TableCell>
                <TableCell>
                  {r.wallet_type === "cash"
                    ? <span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded">Cash</span>
                    : <span className="text-xs bg-amber-50 text-amber-800 px-2 py-0.5 rounded">Reward</span>}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {r.wallet_type === "cash" ? inr(r.amount) : `${r.amount} pts`}
                </TableCell>
                <TableCell><StatusChip s={r.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(r.created_at)}</TableCell>
                <TableCell className="text-xs font-mono">
                  {r.credit_note_no || r.dispatch_advice_no || "—"}
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "pending" ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className={GOLD_BTN} onClick={() => approve(r)} data-testid={`red-approve-${r.redemption_no}`}>
                        <CheckCircle2 size={14} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-700" onClick={() => reject(r)}>
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setDetail(r)}>View</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Redemption {detail?.redemption_no}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div><b>Retailer:</b> {detail.retailer_name}</div>
              <div><b>Distributor:</b> {detail.distributor_name}</div>
              <div><b>Type:</b> {detail.wallet_type}</div>
              <div><b>Amount:</b> {detail.wallet_type === "cash" ? inr(detail.amount) : `${detail.amount} pts`}</div>
              <div><b>Status:</b> <StatusChip s={detail.status} /></div>
              {detail.credit_note_no && <div><b>Credit Note:</b> {detail.credit_note_no}</div>}
              {detail.dispatch_advice_no && <div><b>Dispatch Advice:</b> {detail.dispatch_advice_no}</div>}
              {detail.notes && <div className="bg-slate-50 rounded p-2"><b>Notes:</b> {detail.notes}</div>}
              {detail.rejected_reason && <div className="bg-rose-50 rounded p-2"><b>Rejected:</b> {detail.rejected_reason}</div>}
              <div className="text-xs text-slate-500">Created {niceDate(detail.created_at)} · Actioned {niceDate(detail.approved_at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <NewRedemptionDialog open={newOpen} onClose={() => setNewOpen(false)} onDone={load} />
    </div>
  );
}

function NewRedemptionDialog({ open, onClose, onDone }) {
  const [retailers, setRetailers] = useState([]);
  const [rid, setRid] = useState("");
  const [wtype, setWtype] = useState("cash");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [walletCheck, setWalletCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRid(""); setWtype("cash"); setAmount(""); setNotes(""); setWalletCheck(null);
      dms.cpnReportsWalletSummary().then(r => setRetailers(r.data || [])).catch(() => setRetailers([]));
    }
  }, [open]);

  useEffect(() => {
    if (!rid) { setWalletCheck(null); return; }
    const row = retailers.find(r => r.retailer_id === rid && r.wallet_type === wtype);
    setWalletCheck(row || { balance: 0 });
  }, [rid, wtype, retailers]);

  const retList = useMemo(() => {
    const seen = new Set(); const out = [];
    retailers.forEach(r => {
      if (!seen.has(r.retailer_id)) { seen.add(r.retailer_id); out.push(r); }
    });
    return out.sort((a, b) => (a.retailer_name || "").localeCompare(b.retailer_name || ""));
  }, [retailers]);

  const submit = async () => {
    if (!rid) { toast.error("Pick a retailer"); return; }
    const v = Number(amount);
    if (!v || v <= 0) { toast.error("Enter amount"); return; }
    setBusy(true);
    try {
      await dms.cpnCreateRedemption({
        retailer_id: rid, wallet_type: wtype,
        amount: v, notes: notes.trim(),
      });
      toast.success("Redemption request created (pending approval)");
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Redemption Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Retailer</Label>
            <Select value={rid} onValueChange={setRid}>
              <SelectTrigger data-testid="new-red-retailer"><SelectValue placeholder="Pick retailer" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {retList.map(r => (
                  <SelectItem key={r.retailer_id} value={r.retailer_id}>
                    {r.retailer_name} <span className="text-xs text-slate-500 ml-1">({r.distributor_name})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Wallet</Label>
              <Select value={wtype} onValueChange={setWtype}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="reward">Reward</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} data-testid="new-red-amount" />
            </div>
          </div>
          {walletCheck && (
            <div className="text-xs bg-slate-50 rounded p-2">
              Available balance:{" "}
              <b>{wtype === "cash" ? inr(walletCheck.balance) : `${walletCheck.balance} pts`}</b>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className={GOLD_BTN} data-testid="new-red-submit">
            {busy ? "Creating…" : "Create Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════ OWNER: Credit Notes + Dispatch Advices ═══════════
export function OwnerCreditNotesPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { dms.cpnCreditNotes().then(r => setRows(r.data || [])); }, []);
  return (
    <div>
      <PageHeader title="Credit Notes" subtitle="Issued on cash coupon redemption approval — automatically reduces distributor outstanding" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>CN No.</TableHead><TableHead>Redemption</TableHead>
            <TableHead>Retailer</TableHead><TableHead>Distributor</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Issued</TableHead><TableHead>By</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No credit notes yet</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs font-semibold">{r.cn_no}</TableCell>
                <TableCell className="font-mono text-xs">{r.redemption_no}</TableCell>
                <TableCell>{r.retailer_name}</TableCell>
                <TableCell className="text-xs">{r.distributor_name}</TableCell>
                <TableCell className="text-right font-semibold">{inr(r.amount)}</TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(r.issued_at)}</TableCell>
                <TableCell className="text-xs">{r.issued_by_name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function OwnerDispatchAdvicesPage() {
  const [rows, setRows] = useState([]);
  const load = () => dms.cpnDispatchAdvices().then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const markSent = async (id) => {
    if (!window.confirm("Mark this Dispatch Advice as dispatched?")) return;
    try { await dms.cpnMarkDispatched(id); toast.success("Marked dispatched"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <PageHeader title="Dispatch Advices" subtitle="Issued on reward points redemption — free stock to be sent to distributor" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>DA No.</TableHead><TableHead>Redemption</TableHead>
            <TableHead>Retailer</TableHead><TableHead>Distributor</TableHead>
            <TableHead className="text-right">Points</TableHead>
            <TableHead>Status</TableHead><TableHead>Issued</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No dispatch advices</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs font-semibold">{r.da_no}</TableCell>
                <TableCell className="font-mono text-xs">{r.redemption_no}</TableCell>
                <TableCell>{r.retailer_name}</TableCell>
                <TableCell className="text-xs">{r.distributor_name}</TableCell>
                <TableCell className="text-right font-semibold">{r.points} pts</TableCell>
                <TableCell><StatusChip s={r.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(r.issued_at)}</TableCell>
                <TableCell className="text-right">
                  {r.status === "issued" && (
                    <Button size="sm" className={GOLD_BTN} onClick={() => markSent(r.id)}>
                      <Truck size={14} className="mr-1" /> Mark Dispatched
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═════════════════════════ OWNER: Reports + Audit Log ═══════════════════════
export function OwnerCouponReportsPage() {
  const [tab, setTab] = useState("wallets");
  const [wallets, setWallets] = useState([]);
  const [sp, setSp] = useState([]);
  const [fraud, setFraud] = useState([]);
  const [dup, setDup] = useState([]);

  useEffect(() => {
    dms.cpnReportsWalletSummary().then(r => setWallets(r.data || []));
    dms.cpnReportsSalesperson().then(r => setSp(r.data || []));
    dms.cpnReportsFraud().then(r => setFraud(r.data || []));
    dms.cpnReportsDuplicate().then(r => setDup(r.data || []));
  }, []);

  const tabs = [
    { id: "wallets", label: "Retailer Wallets" },
    { id: "salesperson", label: "Sales Officer Performance" },
    { id: "duplicate", label: `Duplicate Scans${dup.length ? ` (${dup.length})` : ""}` },
    { id: "fraud", label: `Fraud Attempts${fraud.length ? ` (${fraud.length})` : ""}` },
  ];

  return (
    <div>
      <PageHeader title="Coupon Reports" subtitle="Wallet summary, Sales Officer performance, and fraud logs" />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === "wallets" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Retailer</TableHead><TableHead>Distributor</TableHead>
              <TableHead>Wallet</TableHead><TableHead className="text-right">Balance</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {wallets.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No wallets yet</TableCell></TableRow>}
              {wallets.map((w, i) => (
                <TableRow key={i}>
                  <TableCell>{w.retailer_name}</TableCell>
                  <TableCell className="text-xs">{w.distributor_name}</TableCell>
                  <TableCell>
                    {w.wallet_type === "cash"
                      ? <span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded">Cash</span>
                      : <span className="text-xs bg-amber-50 text-amber-800 px-2 py-0.5 rounded">Reward</span>}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {w.wallet_type === "cash" ? inr(w.balance) : `${w.balance} pts`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "salesperson" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Sales Officer</TableHead>
              <TableHead className="text-right">Total Scans</TableHead>
              <TableHead className="text-right">Cash Value</TableHead>
              <TableHead className="text-right">Reward Points</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sp.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No scans yet</TableCell></TableRow>}
              {sp.map((r, i) => (
                <TableRow key={r.salesperson_id}>
                  <TableCell>{i === 0 && <Trophy size={12} className="inline mr-1 text-amber-500" />}{r.salesperson_name}</TableCell>
                  <TableCell className="text-right font-semibold">{r.scans}</TableCell>
                  <TableCell className="text-right">{inr(r.cash_value)}</TableCell>
                  <TableCell className="text-right">{r.reward_points} pts</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "duplicate" && <FraudTable rows={dup} />}
      {tab === "fraud" && <FraudTable rows={fraud} />}
    </div>
  );
}

function FraudTable({ rows }) {
  return (
    <Card className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>When</TableHead><TableHead>Coupon</TableHead>
          <TableHead>Reason</TableHead><TableHead>By</TableHead>
          <TableHead>Retailer</TableHead><TableHead>Distributor</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">✓ No entries</TableCell></TableRow>}
          {rows.map(f => (
            <TableRow key={f.id} className="bg-rose-50/20">
              <TableCell className="text-xs text-slate-500">{niceDate(f.at)}</TableCell>
              <TableCell className="font-mono text-xs">{f.coupon_code}</TableCell>
              <TableCell>
                <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">
                  {f.reason.replace(/_/g, " ")}
                </span>
              </TableCell>
              <TableCell className="text-xs">{f.actor_name} <span className="text-slate-400">({f.actor_role})</span></TableCell>
              <TableCell className="text-xs font-mono">{f.retailer_id || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{f.distributor_id || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export function OwnerCouponAuditLogPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { dms.cpnAuditLog().then(r => setRows(r.data || [])); }, []);
  return (
    <div>
      <PageHeader title="Coupon Audit Log" subtitle="Immutable audit trail — every state change is recorded" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Event</TableHead>
            <TableHead>Entity</TableHead><TableHead>Actor</TableHead>
            <TableHead>Details</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No entries</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{niceDate(r.at)}</TableCell>
                <TableCell className="text-xs font-semibold">{r.event}</TableCell>
                <TableCell className="text-xs">{r.entity_type} <span className="font-mono text-slate-500">{(r.entity_id || "").slice(0, 12)}</span></TableCell>
                <TableCell className="text-xs">{r.actor_name} <span className="text-slate-400">({r.actor_role})</span></TableCell>
                <TableCell className="text-[11px] text-slate-600">
                  <pre className="whitespace-pre-wrap font-mono">{JSON.stringify(r.details || {}, null, 0)}</pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═════════════════════════ SALES OFFICER: Scan Flow ═════════════════════════
export function SalesOfficerScanPageOld() {
  const [retailers, setRetailers] = useState([]);
  const [selectedRid, setSelectedRid] = useState("");
  const [code, setCode] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [preview, setPreview] = useState(null);   // scan preview (fraud + fields)
  const [scanned, setScanned] = useState(null);    // {qr, code} used for preview
  const [search, setSearch] = useState("");
  const [gps, setGps] = useState(null);   // { lat, lng }
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    dms.cpnSoRetailers().then(r => setRetailers(r.data || []))
      .catch(() => toast.error("Failed to load retailers"));
    // best-effort geolocation on mount — permission handled by browser
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setGps(null),
        { maximumAge: 60_000, timeout: 5_000, enableHighAccuracy: false },
      );
    }
  }, []);

  // stable device fingerprint stored in localStorage
  const deviceId = useMemo(() => {
    try {
      let d = window.localStorage.getItem("gooil_device_id");
      if (!d) {
        d = "dev-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.localStorage.setItem("gooil_device_id", d);
      }
      return d;
    } catch { return null; }
  }, []);

  const selectedRet = useMemo(
    () => retailers.find(r => r.id === selectedRid),
    [retailers, selectedRid],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return retailers;
    return retailers.filter(r =>
      (r.name || "").toLowerCase().includes(q)
      || (r.phone || "").includes(q)
      || (r.city || "").toLowerCase().includes(q)
      || (r.shop_name || "").toLowerCase().includes(q),
    );
  }, [retailers, search]);

  const runPreview = async (payloadOverride) => {
    if (!selectedRid) { toast.error("Select a retailer first"); return; }
    const cCode = (code || "").trim().toUpperCase();
    const qr = (payloadOverride ?? qrPayload).trim();
    if (!qr && !cCode) { toast.error("Enter coupon code or paste QR"); return; }
    setBusy(true); setLast(null); setPreview(null);
    try {
      const r = await dms.cpnScanPreview({
        retailer_id: selectedRid,
        qr_payload: qr || undefined,
        coupon_code: cCode || undefined,
        gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
      });
      setScanned({ qr: qr || undefined, code: cCode || undefined });
      setPreview(r.preview || {});
      if (r.fraud) toast.error(`Fraud: ${(r.fraud_reason || "").replace(/_/g, " ")}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Validation failed";
      setPreview(null);
      setLast({ ok: false, message: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const confirmSubmit = async () => {
    if (!preview || preview.fraud || !scanned) return;
    setBusy(true);
    try {
      const r = await dms.cpnScan({
        retailer_id: selectedRid,
        qr_payload: scanned.qr, coupon_code: scanned.code,
        gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
      });
      setLast({ ok: true, ...r });
      setPreview(null); setScanned(null);
      toast.success(r.message);
      setCode(""); setQrPayload("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submit failed";
      setLast({ ok: false, message: msg });
      setPreview(null);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const resetPreview = () => { setPreview(null); setScanned(null); };

  return (
    <div>
      <PageHeader
        title="Scan Coupon"
        subtitle="Select retailer → distributor auto-fetched → scan QR or type code. Balance credits to retailer's wallet."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Retailer picker */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Store className="text-[#a67c00]" size={18} />
            <h3 className="font-bold text-slate-900">Step 1 · Select Retailer</h3>
          </div>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search retailer" className="pl-9" data-testid="so-search" />
          </div>
          <div className="max-h-80 overflow-y-auto rounded border border-slate-100 divide-y divide-slate-50">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">
                No retailers assigned. Ask Owner/TL to assign you a distributor.
              </div>
            )}
            {filtered.map(r => (
              <button key={r.id}
                      onClick={() => setSelectedRid(r.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-amber-50/50 flex items-center gap-2 ${selectedRid === r.id ? "bg-amber-50" : ""}`}
                      data-testid={`so-ret-${r.id}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${selectedRid === r.id ? "bg-[#c9a227] text-white" : "bg-slate-100 text-slate-700"}`}>
                  {(r.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.shop_name} · {r.city} · {r.phone}</div>
                </div>
                <div className="text-[10px] text-slate-500 text-right">
                  <div>Distributor</div>
                  <div className="font-semibold">{r.distributor_name || "—"}</div>
                </div>
              </button>
            ))}
          </div>

          {selectedRet && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
              <div className="font-semibold text-emerald-900 flex items-center gap-1"><CheckCircle2 size={14} /> Selected</div>
              <div><b>{selectedRet.name}</b> — {selectedRet.shop_name || "Retailer"}</div>
              <div className="text-xs text-slate-600">Distributor auto-fetched: <b>{selectedRet.distributor_name}</b></div>
            </div>
          )}
        </Card>

        {/* RIGHT: Scan */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="text-[#a67c00]" size={18} />
            <h3 className="font-bold text-slate-900">Step 2 · Scan / Enter Coupon</h3>
          </div>

          <Label>QR Payload (paste from scanner)</Label>
          <div className="flex gap-2 mt-1 mb-2">
            <Input value={qrPayload} onChange={e => setQrPayload(e.target.value)}
                   placeholder="GOOIL2:xxxxx (paste from scanner)"
                   onKeyDown={e => e.key === "Enter" && runPreview()}
                   data-testid="so-qr-input" />
            <Button className={GOLD_BTN} disabled={busy || !selectedRid || !qrPayload.trim()}
                    onClick={() => runPreview()} data-testid="so-qr-scan">
              {busy ? "…" : "Validate"}
            </Button>
          </div>
          <Button variant="outline" className="w-full mb-3 gap-2"
                  disabled={!selectedRid} onClick={() => setScanOpen(true)}
                  data-testid="so-open-camera">
            <ScanLine size={16} /> Scan with Camera
          </Button>
          <QrScanner open={scanOpen} onClose={() => setScanOpen(false)}
                     onResult={(text) => { setScanOpen(false); setQrPayload(text); runPreview(text); }} />

          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-slate-200"></div>
            <span className="mx-3 text-xs text-slate-400">OR enter manually</span>
            <div className="flex-grow border-t border-slate-200"></div>
          </div>

          <Label>Coupon Code</Label>
          <div className="flex gap-2 mt-1">
            <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                   placeholder="ABCDE001"
                   onKeyDown={e => e.key === "Enter" && runPreview()}
                   data-testid="so-code-input" className="font-mono" />
            <Button className={GOLD_BTN} disabled={busy || !selectedRid || !code.trim()}
                    onClick={() => runPreview()} data-testid="so-code-scan">
              Validate
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Validate shows all details + fraud check first. Wallet is credited only after you press Submit.
          </p>

          {/* PREVIEW → confirm before submit */}
          {preview && (
            <div className={`mt-4 p-4 rounded-lg border ${preview.fraud ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`}
                 data-testid="so-preview">
              <div className="flex items-center gap-2 mb-2">
                {preview.fraud
                  ? <ShieldAlert size={20} className="text-rose-600" />
                  : <ScanLine size={20} className="text-[#a67c00]" />}
                <h4 className="font-bold text-slate-900">Confirm before submit</h4>
                <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${preview.fraud ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`}>
                  FRAUD: {preview.fraud ? "YES" : "NO"}
                </span>
              </div>
              {preview.fraud && (
                <div className="mb-2 text-sm text-rose-700 font-semibold">
                  Reason: {(preview.fraud_reason || "").replace(/_/g, " ")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <PreviewKV k="Logged-in User" v={preview.logged_in_user} />
                <PreviewKV k="Role" v={preview.user_role} />
                <PreviewKV k="Sales Officer" v={preview.sales_officer} />
                <PreviewKV k="Distributor" v={preview.distributor_name} />
                <PreviewKV k="Retailer" v={preview.retailer_name} />
                <PreviewKV k="Box Number" v={preview.box_number || "—"} />
                <PreviewKV k="Coupon Serial" v={preview.coupon_serial} />
                <PreviewKV k="Coupon Type" v={preview.coupon_type} />
                <PreviewKV k="Coupon Value" v={preview.coupon_type === "cash" ? `₹${preview.coupon_value}` : preview.coupon_value} />
                <PreviewKV k="Reward Points" v={preview.reward_points ?? "—"} />
                {preview.coupon_type === "cash" && !preview.fraud && (
                  <>
                    <PreviewKV k="Current Outstanding" v={
                      (preview.current_credit || 0) > 0
                        ? `Credit ₹${preview.current_credit}`
                        : `₹${preview.current_outstanding ?? 0}`} />
                    <PreviewKV k="After This Coupon" v={
                      (preview.projected_credit || 0) > 0
                        ? `Credit ₹${preview.projected_credit}`
                        : `₹${preview.projected_outstanding ?? 0}`} />
                  </>
                )}
              </div>
              {preview.coupon_type === "cash" && !preview.fraud && (
                <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  This ₹{preview.coupon_value} cash coupon will be adjusted against{" "}
                  <b>{preview.retailer_name}</b>{"'s"} outstanding with the distributor.
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button className={GOLD_BTN} disabled={busy || preview.fraud}
                        onClick={confirmSubmit} data-testid="so-submit">
                  {busy ? "Submitting…" : (preview.coupon_type === "cash" ? "Submit & Apply to Ledger" : "Submit & Credit Wallet")}
                </Button>
                <Button variant="outline" onClick={resetPreview} disabled={busy}>Cancel</Button>
              </div>
            </div>
          )}

          {last && (
            <div className={`mt-4 p-4 rounded-lg border ${last.ok ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}
                 data-testid="so-result">
              <div className="flex items-start gap-2">
                {last.ok
                  ? <CheckCircle2 size={22} className="text-emerald-600 mt-0.5" />
                  : <XCircle size={22} className="text-rose-600 mt-0.5" />}
                <div className="flex-1">
                  <div className={`font-semibold ${last.ok ? "text-emerald-900" : "text-rose-900"}`}>
                    {last.ok ? "Coupon Claimed" : "Scan Rejected"}
                  </div>
                  {last.ok && (
                    <div className="text-sm mt-1 space-y-1">
                      <div><span className="font-mono font-bold">{last.coupon_code}</span></div>
                      <div>
                        {last.coupon_type === "cash" ? (
                          <><b className="text-emerald-700">{inr(last.coupon_value)}</b> applied to outstanding</>
                        ) : (
                          <>+<b className="text-emerald-700">{last.coupon_value} pts</b> to Reward Wallet</>
                        )}
                      </div>
                      <div className="text-xs text-slate-600">Retailer: {last.retailer_name} · Distributor: {last.distributor_name}</div>
                      {last.coupon_type === "cash" ? (
                        <div className="text-xs">
                          {(last.new_credit || 0) > 0
                            ? <>Credit balance: <b>{inr(last.new_credit)}</b></>
                            : <>New outstanding: <b>{inr(last.new_outstanding || 0)}</b></>}
                        </div>
                      ) : (
                        <div className="text-xs">New reward balance: <b>{last.new_balance} pts</b></div>
                      )}
                    </div>
                  )}
                  {!last.ok && <div className="text-sm text-slate-700 mt-1">{last.message}</div>}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ═══════════════ RETAILER: Self-Scan (only when owner enables) ══════════════
// Same preview → confirm → submit flow as the Sales Officer scan, but the
// retailer scans on their own behalf — distributor is auto-fetched from their
// own profile, so there is no retailer picker.
export function RetailerScanPage() {
  const [code, setCode] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanned, setScanned] = useState(null);
  const [gps, setGps] = useState(null);
  const [permOk, setPermOk] = useState(null);   // null=loading, true/false
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    dms.cpnGetScanPermission()
      .then(p => setPermOk(!!p.retailer_scan_enabled))
      .catch(() => setPermOk(false));
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setGps(null),
        { maximumAge: 60_000, timeout: 5_000, enableHighAccuracy: false },
      );
    }
  }, []);

  const deviceId = useMemo(() => {
    try {
      let d = window.localStorage.getItem("gooil_device_id");
      if (!d) {
        d = "dev-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.localStorage.setItem("gooil_device_id", d);
      }
      return d;
    } catch { return null; }
  }, []);

  const runPreview = async (payloadOverride) => {
    const cCode = (code || "").trim().toUpperCase();
    const qr = (payloadOverride ?? qrPayload ?? "").trim();
    if (!qr && !cCode) { toast.error("Enter coupon code or paste QR"); return; }
    setBusy(true); setLast(null); setPreview(null);
    try {
      const r = await dms.cpnRetailerScanPreview({
        qr_payload: qr || undefined, coupon_code: cCode || undefined,
        gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
      });
      setScanned({ qr: qr || undefined, code: cCode || undefined });
      setPreview(r.preview || {});
      if (r.fraud) toast.error(`Fraud: ${(r.fraud_reason || "").replace(/_/g, " ")}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Validation failed";
      setPreview(null); setLast({ ok: false, message: msg }); toast.error(msg);
    } finally { setBusy(false); }
  };

  const confirmSubmit = async () => {
    if (!preview || preview.fraud || !scanned) return;
    setBusy(true);
    try {
      const r = await dms.cpnRetailerScan({
        qr_payload: scanned.qr, coupon_code: scanned.code,
        gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
      });
      setLast({ ok: true, ...r });
      setPreview(null); setScanned(null);
      toast.success(r.message);
      setCode(""); setQrPayload("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submit failed";
      setLast({ ok: false, message: msg }); setPreview(null); toast.error(msg);
    } finally { setBusy(false); }
  };

  const resetPreview = () => { setPreview(null); setScanned(null); };

  if (permOk === false) {
    return (
      <div>
        <PageHeader title="Scan Coupon" />
        <Card className="p-8 text-center">
          <ShieldAlert size={28} className="text-rose-500 mx-auto mb-2" />
          <div className="font-semibold text-slate-900">Scanning is currently disabled</div>
          <div className="text-sm text-slate-500 mt-1">
            The company owner has not enabled retailer self-scanning. Please contact your Sales Officer.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Scan Coupon"
        subtitle="Scan a QR or type the coupon code. Points/cash credit to your own wallet after you Submit."
      />
      <div className="max-w-xl">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="text-[#a67c00]" size={18} />
            <h3 className="font-bold text-slate-900">Scan / Enter Coupon</h3>
          </div>

          <Label>QR Payload (paste from scanner)</Label>
          <div className="flex gap-2 mt-1 mb-2">
            <Input value={qrPayload} onChange={e => setQrPayload(e.target.value)}
                   placeholder="GOOIL2:xxxxx (paste from scanner)"
                   onKeyDown={e => e.key === "Enter" && runPreview()}
                   data-testid="ret-qr-input" />
            <Button className={GOLD_BTN} disabled={busy || !qrPayload.trim()}
                    onClick={() => runPreview()} data-testid="ret-qr-scan">
              {busy ? "…" : "Validate"}
            </Button>
          </div>
          <Button variant="outline" className="w-full mb-3 gap-2" onClick={() => setScanOpen(true)} data-testid="ret-open-camera">
            <ScanLine size={16} /> Scan with Camera
          </Button>
          <QrScanner open={scanOpen} onClose={() => setScanOpen(false)}
                     onResult={(text) => { setScanOpen(false); setQrPayload(text); runPreview(text); }} />

          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-slate-200"></div>
            <span className="mx-3 text-xs text-slate-400">OR enter manually</span>
            <div className="flex-grow border-t border-slate-200"></div>
          </div>

          <Label>Coupon Code</Label>
          <div className="flex gap-2 mt-1">
            <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                   placeholder="ABCDE001"
                   onKeyDown={e => e.key === "Enter" && runPreview()}
                   data-testid="ret-code-input" className="font-mono" />
            <Button className={GOLD_BTN} disabled={busy || !code.trim()}
                    onClick={() => runPreview()} data-testid="ret-code-scan">
              Validate
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Validate shows all details + fraud check first. Wallet is credited only after you press Submit.
          </p>

          {preview && (
            <div className={`mt-4 p-4 rounded-lg border ${preview.fraud ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`}
                 data-testid="ret-preview">
              <div className="flex items-center gap-2 mb-2">
                {preview.fraud
                  ? <ShieldAlert size={20} className="text-rose-600" />
                  : <ScanLine size={20} className="text-[#a67c00]" />}
                <h4 className="font-bold text-slate-900">Confirm before submit</h4>
                <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${preview.fraud ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`}>
                  FRAUD: {preview.fraud ? "YES" : "NO"}
                </span>
              </div>
              {preview.fraud && (
                <div className="mb-2 text-sm text-rose-700 font-semibold">
                  Reason: {(preview.fraud_reason || "").replace(/_/g, " ")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <PreviewKV k="Logged-in User" v={preview.logged_in_user} />
                <PreviewKV k="Role" v={preview.user_role} />
                <PreviewKV k="Distributor" v={preview.distributor_name} />
                <PreviewKV k="Retailer" v={preview.retailer_name} />
                <PreviewKV k="Box Number" v={preview.box_number || "—"} />
                <PreviewKV k="Coupon Serial" v={preview.coupon_serial} />
                <PreviewKV k="Coupon Type" v={preview.coupon_type} />
                <PreviewKV k="Coupon Value" v={preview.coupon_type === "cash" ? `₹${preview.coupon_value}` : preview.coupon_value} />
                <PreviewKV k="Reward Points" v={preview.reward_points ?? "—"} />
              </div>
              <div className="flex gap-2 mt-3">
                <Button className={GOLD_BTN} disabled={busy || preview.fraud}
                        onClick={confirmSubmit} data-testid="ret-submit">
                  {busy ? "Submitting…" : "Submit & Credit Wallet"}
                </Button>
                <Button variant="outline" onClick={resetPreview} disabled={busy}>Cancel</Button>
              </div>
            </div>
          )}

          {last && (
            <div className={`mt-4 p-4 rounded-lg border ${last.ok ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}
                 data-testid="ret-result">
              <div className="flex items-start gap-2">
                {last.ok
                  ? <CheckCircle2 size={22} className="text-emerald-600 mt-0.5" />
                  : <XCircle size={22} className="text-rose-600 mt-0.5" />}
                <div className="flex-1">
                  <div className={`font-semibold ${last.ok ? "text-emerald-900" : "text-rose-900"}`}>
                    {last.ok ? "Coupon Claimed" : "Scan Rejected"}
                  </div>
                  {last.ok && (
                    <div className="text-sm mt-1 space-y-1">
                      <div><span className="font-mono font-bold">{last.coupon_serial}</span></div>
                      <div>
                        {last.coupon_type === "cash" ? (
                          <>+<b className="text-emerald-700">{inr(last.coupon_value)}</b> to Cash Wallet</>
                        ) : (
                          <>+<b className="text-emerald-700">{last.coupon_value} pts</b> to Reward Wallet</>
                        )}
                      </div>
                      <div className="text-xs">New {last.wallet_type} balance: <b>{last.wallet_type === "cash" ? inr(last.new_balance) : `${last.new_balance} pts`}</b></div>
                    </div>
                  )}
                  {!last.ok && <div className="text-sm text-slate-700 mt-1">{last.message}</div>}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


// ═════════════════════ DISTRIBUTOR: Self-Scan (own wallet) ══════════════════
export function DistributorScanPageOld() {
  const [code, setCode] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanned, setScanned] = useState(null);
  const [gps, setGps] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const loadWallet = () => dms.cpnDistWallet().then(setWallet).catch(() => {});
  useEffect(() => {
    loadWallet();
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setGps(null),
        { maximumAge: 60_000, timeout: 5_000, enableHighAccuracy: false },
      );
    }
  }, []);

  const deviceId = useMemo(() => {
    try {
      let d = window.localStorage.getItem("gooil_device_id");
      if (!d) { d = "dev-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36); window.localStorage.setItem("gooil_device_id", d); }
      return d;
    } catch { return null; }
  }, []);

  const runPreview = async (payloadOverride) => {
    const cCode = (code || "").trim().toUpperCase();
    const qr = (payloadOverride ?? qrPayload ?? "").trim();
    if (!qr && !cCode) { toast.error("Enter coupon code or paste QR"); return; }
    setBusy(true); setLast(null); setPreview(null);
    try {
      const r = await dms.cpnDistScanPreview({ qr_payload: qr || undefined, coupon_code: cCode || undefined, gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId });
      setScanned({ qr: qr || undefined, code: cCode || undefined });
      setPreview(r.preview || {});
      if (r.fraud) toast.error(`Fraud: ${(r.fraud_reason || "").replace(/_/g, " ")}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Validation failed";
      setPreview(null); setLast({ ok: false, message: msg }); toast.error(msg);
    } finally { setBusy(false); }
  };

  const confirmSubmit = async () => {
    if (!preview || preview.fraud || !scanned) return;
    setBusy(true);
    try {
      const r = await dms.cpnDistScan({ qr_payload: scanned.qr, coupon_code: scanned.code, gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId });
      setLast({ ok: true, ...r });
      setPreview(null); setScanned(null); setCode(""); setQrPayload("");
      toast.success(r.message);
      loadWallet();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submit failed";
      setLast({ ok: false, message: msg }); setPreview(null); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Scan Coupon" subtitle="Scan a QR or type the coupon code. Cash/points credit to your own wallet after Submit." />
      {wallet && (
        <div className="grid grid-cols-2 gap-3 max-w-xl mb-4">
          <Card className="p-4"><div className="text-xs text-slate-500">Cash Wallet</div><div className="text-xl font-bold text-emerald-700">{inr(wallet.cash_wallet?.balance || 0)}</div></Card>
          <Card className="p-4"><div className="text-xs text-slate-500">Reward Wallet</div><div className="text-xl font-bold text-[#a67c00]">{wallet.reward_wallet?.balance || 0} pts</div></Card>
        </div>
      )}
      <div className="max-w-xl">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="text-[#a67c00]" size={18} />
            <h3 className="font-bold text-slate-900">Scan / Enter Coupon</h3>
          </div>
          <Label>QR Payload (paste from scanner)</Label>
          <div className="flex gap-2 mt-1 mb-2">
            <Input value={qrPayload} onChange={e => setQrPayload(e.target.value)} placeholder="GOOIL2:xxxxx (paste from scanner)"
                   onKeyDown={e => e.key === "Enter" && runPreview()} data-testid="dist-qr-input" />
            <Button className={GOLD_BTN} disabled={busy || !qrPayload.trim()} onClick={() => runPreview()} data-testid="dist-qr-scan">{busy ? "…" : "Validate"}</Button>
          </div>
          <Button variant="outline" className="w-full mb-3 gap-2" onClick={() => setScanOpen(true)} data-testid="dist-open-camera">
            <ScanLine size={16} /> Scan with Camera
          </Button>
          <QrScanner open={scanOpen} onClose={() => setScanOpen(false)}
                     onResult={(text) => { setScanOpen(false); setQrPayload(text); runPreview(text); }} />
          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-slate-200"></div>
            <span className="mx-3 text-xs text-slate-400">OR enter manually</span>
            <div className="flex-grow border-t border-slate-200"></div>
          </div>
          <Label>Coupon Code</Label>
          <div className="flex gap-2 mt-1">
            <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="ABCDE001"
                   onKeyDown={e => e.key === "Enter" && runPreview()} data-testid="dist-code-input" className="font-mono" />
            <Button className={GOLD_BTN} disabled={busy || !code.trim()} onClick={() => runPreview()} data-testid="dist-code-scan">Validate</Button>
          </div>

          {preview && (
            <div className={`mt-4 p-4 rounded-lg border ${preview.fraud ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`} data-testid="dist-preview">
              <div className="flex items-center gap-2 mb-2">
                {preview.fraud ? <ShieldAlert size={20} className="text-rose-600" /> : <ScanLine size={20} className="text-[#a67c00]" />}
                <h4 className="font-bold text-slate-900">Confirm before submit</h4>
                <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${preview.fraud ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`}>FRAUD: {preview.fraud ? "YES" : "NO"}</span>
              </div>
              {preview.fraud && <div className="mb-2 text-sm text-rose-700 font-semibold">Reason: {(preview.fraud_reason || "").replace(/_/g, " ")}</div>}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <PreviewKV k="Logged-in User" v={preview.logged_in_user} />
                <PreviewKV k="Role" v={preview.user_role} />
                <PreviewKV k="Distributor" v={preview.distributor_name} />
                <PreviewKV k="Box Number" v={preview.box_number || "—"} />
                <PreviewKV k="Coupon Serial" v={preview.coupon_serial} />
                <PreviewKV k="Coupon Type" v={preview.coupon_type} />
                <PreviewKV k="Coupon Value" v={preview.coupon_type === "cash" ? `₹${preview.coupon_value}` : preview.coupon_value} />
              </div>
              <div className="flex gap-2 mt-3">
                <Button className={GOLD_BTN} disabled={busy || preview.fraud} onClick={confirmSubmit} data-testid="dist-submit">{busy ? "Submitting…" : "Submit & Credit Wallet"}</Button>
                <Button variant="outline" onClick={() => { setPreview(null); setScanned(null); }} disabled={busy}>Cancel</Button>
              </div>
            </div>
          )}

          {last && (
            <div className={`mt-4 p-4 rounded-lg border ${last.ok ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`} data-testid="dist-result">
              <div className="flex items-start gap-2">
                {last.ok ? <CheckCircle2 size={22} className="text-emerald-600 mt-0.5" /> : <XCircle size={22} className="text-rose-600 mt-0.5" />}
                <div className="flex-1">
                  <div className={`font-semibold ${last.ok ? "text-emerald-900" : "text-rose-900"}`}>{last.ok ? "Coupon Claimed" : "Scan Rejected"}</div>
                  {last.ok && (
                    <div className="text-sm mt-1 space-y-1">
                      <div><span className="font-mono font-bold">{last.coupon_serial}</span></div>
                      <div>{last.coupon_type === "cash" ? <>+<b className="text-emerald-700">{inr(last.coupon_value)}</b> to Cash Wallet</> : <>+<b className="text-emerald-700">{last.coupon_value} pts</b> to Reward Wallet</>}</div>
                      <div className="text-xs">New {last.wallet_type} balance: <b>{last.wallet_type === "cash" ? inr(last.new_balance) : `${last.new_balance} pts`}</b></div>
                    </div>
                  )}
                  {!last.ok && <div className="text-sm text-slate-700 mt-1">{last.message}</div>}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


// ═════════════════ OWNER: Coupon Scan Audit (who scanned + designation) ══════
export function OwnerScanAuditPage() {
  const [rows, setRows] = useState([]);
  const [channel, setChannel] = useState("");
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    dms.cpnScanAudit(channel ? { channel } : {})
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [channel]);
  const CH_LABEL = { distributor_self_scan: "Distributor Scan", retailer_self_scan: "Retailer Scan", salesperson_scan: "Salesperson Scan" };
  return (
    <div>
      <PageHeader title="Coupon Scan Audit" subtitle="Every scan with the scanner's login and designation" />
      <div className="flex gap-2 mb-3 flex-wrap">
        {["", "distributor_self_scan", "retailer_self_scan", "salesperson_scan"].map(c => (
          <button key={c || "all"} onClick={() => setChannel(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${channel === c ? "bg-[#a67c00] text-white border-[#a67c00]" : "bg-white text-slate-600 border-slate-200"}`}>
            {c ? CH_LABEL[c] : "All"}
          </button>
        ))}
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Serial</TableHead>
            <TableHead>Type / Value</TableHead><TableHead>Scanned By (Login)</TableHead>
            <TableHead>Designation</TableHead><TableHead>Retailer / Distributor</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No scans yet</TableCell></TableRow>}
            {!loading && rows.map(r => (
              <TableRow key={r.coupon_id}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{niceDate(r.scanned_at)}</TableCell>
                <TableCell className="text-xs font-mono font-semibold">{r.serial}</TableCell>
                <TableCell className="text-xs">{r.coupon_type === "cash" ? <b className="text-emerald-700">{inr(r.coupon_value)}</b> : <b className="text-[#a67c00]">{r.coupon_value} pts</b>}</TableCell>
                <TableCell className="text-xs">{r.scanned_by_name} <span className="text-slate-400 font-mono">{(r.scanned_by_user_id || "").slice(0, 12)}</span></TableCell>
                <TableCell className="text-xs"><span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">{r.designation}</span></TableCell>
                <TableCell className="text-xs text-slate-600">{r.retailer_name || r.distributor_name || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═════════════════════════ RETAILER: Wallet & History (view-only) ═══════════
export function RetailerWalletPage() {
  const [wallet, setWallet] = useState(null);
  const [tab, setTab] = useState("cash");
  const [txs, setTxs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reds, setReds] = useState([]);

  const load = useCallback(() => {
    dms.cpnRetailerWallet().then(setWallet).catch(() => {});
    dms.cpnRetailerTransactions().then(r => setTxs(r.data || []));
    dms.cpnRetailerCoupons().then(r => setCoupons(r.data || []));
    dms.cpnRetailerRedemptions().then(r => setReds(r.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const wCash = wallet?.cash_wallet || {};
  const wRew = wallet?.reward_wallet || {};

  return (
    <div>
      <PageHeader
        title="My Wallets & Coupons"
        subtitle="Cash & Reward wallets are credited when a Sales Officer scans a coupon for you"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <Card className="p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="text-emerald-700" size={20} />
            <h3 className="font-bold text-slate-900">Cash Wallet</h3>
          </div>
          <div className="text-3xl font-bold text-emerald-800">{inr(wCash.balance || 0)}</div>
          {wCash.pending_redemptions > 0 && (
            <div className="text-xs text-amber-800 mt-2">
              {wCash.pending_redemptions} redemption(s) pending approval
            </div>
          )}
          <div className="text-xs text-slate-500 mt-3">Redeemed as Credit Note against your distributor&apos;s outstanding — approved by Owner/Accountant.</div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <Award className="text-[#a67c00]" size={20} />
            <h3 className="font-bold text-slate-900">Reward Points Wallet</h3>
          </div>
          <div className="text-3xl font-bold text-[#8a6600]">{(wRew.balance || 0).toLocaleString("en-IN")} <span className="text-lg font-normal">pts</span></div>
          {wRew.pending_redemptions > 0 && (
            <div className="text-xs text-amber-800 mt-2">
              {wRew.pending_redemptions} redemption(s) pending approval
            </div>
          )}
          <div className="text-xs text-slate-500 mt-3">Redeemed as free stock via Dispatch Advice — approved by Owner.</div>
        </Card>
      </div>

      <Tabs
        tabs={[
          { id: "cash", label: "Cash Transactions", badge: txs.filter(t => t.wallet_type === "cash").length },
          { id: "reward", label: "Reward Transactions", badge: txs.filter(t => t.wallet_type === "reward").length },
          { id: "coupons", label: "My Coupons", badge: coupons.length },
          { id: "redemptions", label: "Redemptions", badge: reds.length },
        ]}
        value={tab} onChange={setTab}
      />

      {(tab === "cash" || tab === "reward") && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>When</TableHead><TableHead>Type</TableHead>
              <TableHead>Coupon / Reference</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {txs.filter(t => t.wallet_type === tab).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No transactions</TableCell></TableRow>
              )}
              {txs.filter(t => t.wallet_type === tab).map(t => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{niceDate(t.at)}</TableCell>
                  <TableCell>
                    {t.kind === "credit_coupon"
                      ? <span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded">CREDIT · Coupon</span>
                      : <span className="text-xs bg-rose-50 text-rose-800 px-2 py-0.5 rounded">DEBIT · Redemption</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.coupon_code || t.credit_note_no || t.dispatch_advice_no || "—"}
                  </TableCell>
                  <TableCell className="text-xs">{t.created_by_name}</TableCell>
                  <TableCell className={`text-right font-bold ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {t.wallet_type === "cash"
                      ? `${t.amount >= 0 ? "+" : ""}${inr(Math.abs(t.amount))}`
                      : `${t.amount >= 0 ? "+" : "-"}${Math.abs(t.amount)} pts`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "coupons" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Type</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Claimed On</TableHead><TableHead>By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {coupons.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No coupons yet</TableCell></TableRow>}
              {coupons.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs">{c.coupon_code}</TableCell>
                  <TableCell><StatusChip s={c.coupon_type} /></TableCell>
                  <TableCell className="text-right font-semibold">
                    {c.coupon_type === "cash" ? inr(c.coupon_value) : `${c.coupon_value} pts`}
                  </TableCell>
                  <TableCell><StatusChip s={c.status} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(c.claim_timestamp)}</TableCell>
                  <TableCell className="text-xs">{c.claimed_by_user_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "redemptions" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Redemption</TableHead><TableHead>Type</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>CN / DA</TableHead>
              <TableHead>Requested</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reds.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No redemptions</TableCell></TableRow>}
              {reds.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.redemption_no}</TableCell>
                  <TableCell><StatusChip s={r.wallet_type} /></TableCell>
                  <TableCell className="text-right font-semibold">
                    {r.wallet_type === "cash" ? inr(r.amount) : `${r.amount} pts`}
                  </TableCell>
                  <TableCell><StatusChip s={r.status} /></TableCell>
                  <TableCell className="font-mono text-xs">{r.credit_note_no || r.dispatch_advice_no || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ═════════════════════════ DISTRIBUTOR: Wallets + CN + DA (view-only) ═══════
export function DistributorCouponsPage() {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("retailers");
  const [cns, setCns] = useState([]);
  const [das, setDas] = useState([]);
  const [reds, setReds] = useState([]);

  useEffect(() => {
    dms.cpnDistSummary().then(setSummary).catch(() => {});
    dms.cpnDistCreditNotes().then(r => setCns(r.data || []));
    dms.cpnDistDispatchAdvices().then(r => setDas(r.data || []));
    dms.cpnListRedemptions().then(r => setReds(r.data || []));
  }, []);

  return (
    <div>
      <PageHeader title="Coupon Rewards" subtitle="View retailer wallets, credit notes and dispatch advices for your network" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Retailers" value={summary?.retailers?.length || 0} icon={Store} />
        <Kpi label="Credit Notes" value={summary?.credit_notes_count || 0} icon={FileText} />
        <Kpi label="Redemption Pending" value={summary?.pending_redemptions || 0} tint="bg-amber-50 text-amber-700" icon={Activity} />
        <Kpi label="Approved" value={summary?.approved_redemptions || 0} tint="bg-emerald-50 text-emerald-700" icon={CheckCircle2} />
      </div>

      <Tabs tabs={[
        { id: "retailers", label: "Retailer Wallets" },
        { id: "redemptions", label: "Redemptions" },
        { id: "credit", label: "Credit Notes" },
        { id: "dispatch", label: "Dispatch Advices" },
      ]} value={tab} onChange={setTab} />

      {tab === "retailers" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Retailer</TableHead>
              <TableHead className="text-right">Cash Wallet</TableHead>
              <TableHead className="text-right">Reward Wallet</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(summary?.retailers || []).length === 0
                && <TableRow><TableCell colSpan={3} className="text-center py-10 text-slate-400">No retailers</TableCell></TableRow>}
              {(summary?.retailers || []).map(r => (
                <TableRow key={r.retailer_id}>
                  <TableCell className="font-semibold">{r.retailer_name}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-700">{inr(r.cash_balance)}</TableCell>
                  <TableCell className="text-right font-semibold text-[#8a6600]">{r.reward_balance} pts</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "redemptions" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Redemption</TableHead><TableHead>Retailer</TableHead>
              <TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead><TableHead>CN / DA</TableHead>
              <TableHead>When</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reds.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No redemptions</TableCell></TableRow>}
              {reds.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.redemption_no}</TableCell>
                  <TableCell>{r.retailer_name}</TableCell>
                  <TableCell><StatusChip s={r.wallet_type} /></TableCell>
                  <TableCell className="text-right font-semibold">
                    {r.wallet_type === "cash" ? inr(r.amount) : `${r.amount} pts`}
                  </TableCell>
                  <TableCell><StatusChip s={r.status} /></TableCell>
                  <TableCell className="font-mono text-xs">{r.credit_note_no || r.dispatch_advice_no || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "credit" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>CN No.</TableHead><TableHead>Retailer</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Issued</TableHead><TableHead>Redemption</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {cns.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No credit notes</TableCell></TableRow>}
              {cns.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs font-semibold">{r.cn_no}</TableCell>
                  <TableCell>{r.retailer_name}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">{inr(r.amount)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(r.issued_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.redemption_no}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "dispatch" && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>DA No.</TableHead><TableHead>Retailer</TableHead>
              <TableHead className="text-right">Points</TableHead>
              <TableHead>Status</TableHead><TableHead>Issued</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {das.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No dispatch advices</TableCell></TableRow>}
              {das.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs font-semibold">{r.da_no}</TableCell>
                  <TableCell>{r.retailer_name}</TableCell>
                  <TableCell className="text-right font-bold text-[#8a6600]">{r.points} pts</TableCell>
                  <TableCell><StatusChip s={r.status} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(r.issued_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}


// ═════════════════════════ OWNER: Fraud Detection Dashboard ═════════════════
const FRAUD_REASON_LABELS = {
  invalid_code: "Invalid Code (unknown QR)",
  invalid_signature: "Wrong Digital Signature",
  invalid_encryption: "Invalid Encryption / Tampered",
  invalid_hidden_id: "Invalid Hidden Secure ID",
  modified_payload: "Modified / Malformed Payload",
  wrong_version: "Unsupported / Wrong Version",
  wrong_campaign: "QR from Another Campaign",
  online_generator_suspected: "Online Generator (Fake QR)",
  inactive_batch: "QR from Inactive Batch",
  already_claimed: "Already Claimed",
  race_lost: "Race Condition (Concurrent)",
  cancelled: "Coupon Cancelled",
  expired: "Coupon Expired",
  coupon_inactive: "Coupon Inactive",
  so_not_assigned_to_distributor: "SO not assigned to Distributor",
  batch_inactive: "Batch Inactive (legacy)",
  malformed_qr: "Malformed QR (legacy)",
  invalid_token: "Invalid Token (legacy)",
};

export function OwnerFraudDashboardPage() {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, list] = await Promise.all([
        dms.cpnFraudDashboard(),
        dms.cpnReportsFraudFiltered(reason ? { reason } : {}),
      ]);
      setDash(d); setRows(list.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load fraud dashboard");
    } finally { setBusy(false); }
  }, [reason]);

  useEffect(() => { load(); }, [load]);

  const k = dash?.kpis || {};
  const byReason = dash?.by_reason || {};
  const byDist = dash?.by_distributor || [];
  const byActor = dash?.by_actor || [];

  return (
    <div>
      <PageHeader
        title="Fraud Detection Dashboard"
        subtitle="Real-time monitoring of failed coupon scans and suspicious QR activity"
        action={<Button variant="outline" onClick={load} disabled={busy}><RefreshCw size={14} className="mr-1" /> Refresh</Button>}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Today" value={k.today || 0} tint="bg-rose-100 text-rose-800" icon={ShieldAlert} />
        <Kpi label="Last 7 days" value={k.last7 || 0} tint="bg-orange-100 text-orange-800" icon={AlertTriangle} />
        <Kpi label="Last 30 days" value={k.last30 || 0} tint="bg-amber-100 text-amber-800" icon={Activity} />
        <Kpi label="All-time Total" value={k.total || 0} tint="bg-slate-200 text-slate-800" icon={Shield} />
      </div>

      {/* By reason + By distributor + By actor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 font-semibold mb-3">By Reason</div>
          {Object.keys(byReason).length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center">No fraud attempts recorded</div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(byReason).map(([r, n]) => {
                const max = Math.max(...Object.values(byReason));
                const pct = max ? Math.round((n / max) * 100) : 0;
                return (
                  <div key={r}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">{FRAUD_REASON_LABELS[r] || r}</span>
                      <span className="font-semibold text-slate-900">{n}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-rose-400 to-rose-600"
                           style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Top Distributors (fraud attempts)</div>
          {byDist.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center">No distributor data</div>
          ) : (
            <div className="space-y-1.5">
              {byDist.slice(0, 8).map(r => (
                <div key={r.distributor_id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700">{r.distributor_name || r.distributor_id}</span>
                  <span className="font-semibold text-slate-900">{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Top Actors (fraud attempts)</div>
          {byActor.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center">No actor data</div>
          ) : (
            <div className="space-y-1.5">
              {byActor.slice(0, 8).map(r => (
                <div key={r.actor_id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700">
                    {r.actor_name || r.actor_id} <span className="text-slate-400">({r.actor_role || "—"})</span>
                  </span>
                  <span className="font-semibold text-slate-900">{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent fraud attempts table */}
      <Card className="overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">All Fraud Attempts</span>
          <div className="ml-auto flex items-center gap-2">
            <Select value={reason || "__all__"} onValueChange={v => setReason(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-64"><SelectValue placeholder="All reasons" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All reasons</SelectItem>
                {Object.entries(FRAUD_REASON_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Coupon / Serial</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Retailer</TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>GPS</TableHead>
              <TableHead>Device</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">
                No fraud attempts {reason ? `matching this reason` : `on record`}
              </TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-xs text-slate-500">{niceDate(r.at)}</TableCell>
                <TableCell>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">
                    {FRAUD_REASON_LABELS[r.reason] || r.reason}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.coupon_code || "—"}</TableCell>
                <TableCell className="text-xs">
                  {r.actor_name || "—"} <span className="text-slate-400">({r.actor_role || "—"})</span>
                </TableCell>
                <TableCell className="text-xs">{r.retailer_id || "—"}</TableCell>
                <TableCell className="text-xs">{r.distributor_id || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{r.ip_address || "—"}</TableCell>
                <TableCell className="text-xs">
                  {r.gps_lat && r.gps_lng ? `${Number(r.gps_lat).toFixed(3)}, ${Number(r.gps_lng).toFixed(3)}` : "—"}
                </TableCell>
                <TableCell className="text-xs font-mono">{r.device_id ? String(r.device_id).slice(0, 12) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ═════════════════════════ OWNER: Box Management ════════════════════════════
// Flow: Generate → Print → Production → Create Box → Assign coupon range to Box
//       → Assign Box to Distributor. Distributor assignment is on the BOX.
export function OwnerBoxesPage() {
  const [boxes, setBoxes] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignBox, setAssignBox] = useState(null);   // box for assign-coupons dialog
  const [historyBox, setHistoryBox] = useState(null); // box for scan-history dialog
  const [perm, setPerm] = useState(null);
  const [boxDeleteBusyId, setBoxDeleteBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      dms.cpnListBoxes(),
      dms.listDistributors(),
      dms.cpnListBatches(),
      dms.cpnGetScanPermission().catch(() => ({ retailer_scan_enabled: false })),
    ]).then(([b, d, bt, p]) => {
      setBoxes(b.data || []);
      setDistributors(Array.isArray(d) ? d : (d.data || []));
      setBatches((bt.data || bt || []));
      setPerm(!!p.retailer_scan_enabled);
    }).catch(() => toast.error("Failed to load boxes"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createBox = async (distributor_id) => {
    try {
      const r = await dms.cpnCreateBox(distributor_id ? { distributor_id } : {});
      toast.success(`Box ${r.box.box_number} created`);
      setCreateOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const assignDistributor = async (box, distributor_id) => {
    if (!distributor_id) return;
    try {
      const r = await dms.cpnBoxAssignDistributor(box.id, { distributor_id });
      toast.success(`${box.box_number} → distributor assigned (${r.coupons_updated} coupons)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  const togglePerm = async () => {
    try {
      const r = await dms.cpnSetScanPermission(!perm);
      setPerm(r.retailer_scan_enabled);
      toast.success(`Retailer scanning ${r.retailer_scan_enabled ? "ENABLED" : "DISABLED"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const printLabel = async (box) => {
    try {
      toast.message(`Preparing label for ${box.box_number}…`);
      await dms.cpnBoxLabelPdf(box.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Label generation failed"); }
  };

  return (
    <div>
      <PageHeader title="Box Management"
        subtitle="Create production boxes, attach an active coupon range, then assign the box to a distributor."
        action={
          <Button className={GOLD_BTN} onClick={() => setCreateOpen(true)} data-testid="box-create-btn">
            <Plus size={16} className="mr-1" /> Create Box
          </Button>
        } />

      {/* Retailer scan permission toggle */}
      <Card className="p-4 mb-4 flex items-center gap-3">
        <ScanLine className="text-[#a67c00]" size={20} />
        <div className="flex-1">
          <div className="font-bold text-slate-900">Retailer Scan Permission</div>
          <div className="text-xs text-slate-500">
            When ON, retailers see a Scan option in their app and can scan coupons themselves.
          </div>
        </div>
        <span className={`text-xs font-bold px-2 py-0.5 rounded ${perm ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"}`}>
          {perm === null ? "…" : perm ? "ON" : "OFF"}
        </span>
        <Button variant={perm ? "outline" : "default"} className={perm ? "" : GOLD_BTN}
                onClick={togglePerm} data-testid="scan-perm-toggle">
          {perm ? "Disable" : "Enable"}
        </Button>
      </Card>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Box #</TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead>Coupons</TableHead>
              <TableHead>Serial Range</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && boxes.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">
                No boxes yet. Click &quot;Create Box&quot; to start.
              </TableCell></TableRow>
            )}
            {boxes.map(b => (
              <TableRow key={b.id} data-testid={`box-row-${b.box_number}`}>
                <TableCell className="font-mono font-bold text-slate-900">{b.box_number}</TableCell>
                <TableCell>
                  {b.distributor_name
                    ? <span className="text-slate-800">{b.distributor_name}</span>
                    : <span className="text-amber-600 text-xs">Not assigned</span>}
                </TableCell>
                <TableCell>{b.coupon_count || 0}</TableCell>
                <TableCell className="font-mono text-xs">
                  {b.serial_from ? `${b.serial_from} → ${b.serial_to}` : "—"}
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${b.status === "assigned" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {b.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setHistoryBox(b)}
                            data-testid={`box-history-${b.box_number}`}>
                      <Clock size={14} className="mr-1" /> History
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => printLabel(b)}
                            data-testid={`box-label-${b.box_number}`}>
                      <Printer size={14} className="mr-1" /> Label
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAssignBox(b)}
                            data-testid={`box-assign-coupons-${b.box_number}`}>
                      <PackageCheck size={14} className="mr-1" /> Assign Coupons
                    </Button>
                    <Select onValueChange={(v) => assignDistributor(b, v)}>
                      <SelectTrigger className="w-[170px] h-9" data-testid={`box-assign-dist-${b.box_number}`}>
                        <SelectValue placeholder={<span className="flex items-center gap-1"><Truck size={13} /> Assign Distributor</span>} />
                      </SelectTrigger>
                      <SelectContent>
                        {distributors.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50"
                            disabled={boxDeleteBusyId === b.id}
                            onClick={async () => {
                              if (!window.confirm(`Delete box ${b.box_number}? This can be restored within 30 days.`)) return;
                              setBoxDeleteBusyId(b.id);
                              try {
                                await dms.cpnDeleteBox(b.id);
                                toast.success(`Box ${b.box_number} deleted — restorable for 30 days`);
                                load();
                              } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                              finally { setBoxDeleteBusyId(null); }
                            }}
                            data-testid={`box-delete-${b.box_number}`}>
                      <Trash2 size={14} className="mr-1" /> Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <CreateBoxDialog open={createOpen} onClose={() => setCreateOpen(false)}
                       distributors={distributors} onCreate={createBox} />
      <AssignCouponsDialog box={assignBox} batches={batches}
                           onClose={() => setAssignBox(null)}
                           onDone={() => { setAssignBox(null); load(); }} />
      <BoxScanHistoryDialog box={historyBox} onClose={() => setHistoryBox(null)} />
    </div>
  );
}

function BoxScanHistoryDialog({ box, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!box) { setData(null); return; }
    setLoading(true);
    dms.cpnBoxScanHistory(box.id)
      .then(setData)
      .catch(() => toast.error("Failed to load scan history"))
      .finally(() => setLoading(false));
  }, [box]);

  const fmt = (s) => (s ? String(s).slice(0, 19).replace("T", " ") : "—");

  return (
    <Dialog open={!!box} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Scan History — {box?.box_number}</DialogTitle>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-slate-400">Loading…</div>}
        {!loading && data && (
          <>
            <div className="flex gap-3 mb-3 text-sm">
              <span className="px-2 py-1 rounded bg-slate-100">Coupons: <b>{data.coupon_count}</b></span>
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">Claimed: <b>{data.claimed_count}</b></span>
              <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">Pending: <b>{data.pending_count}</b></span>
              {data.box?.distributor_name && (
                <span className="px-2 py-1 rounded bg-slate-100">Distributor: <b>{data.box.distributor_name}</b></span>
              )}
            </div>
            <div className="max-h-[55vh] overflow-y-auto rounded border border-slate-100">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Serial</TableHead>
                    <TableHead>Type / Value</TableHead>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Claimed By</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Where</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.data || []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">
                      No coupons from this box have been claimed yet.
                    </TableCell></TableRow>
                  )}
                  {(data.data || []).map((c, i) => (
                    <TableRow key={i} data-testid={`scan-hist-row-${i}`}>
                      <TableCell className="font-mono text-xs font-bold">{c.visible_serial || c.coupon_code}</TableCell>
                      <TableCell className="text-xs">
                        {c.coupon_type === "cash" ? `Cash ₹${c.coupon_value}` : `${c.coupon_value} pts`}
                      </TableCell>
                      <TableCell className="text-xs">{c.retailer_name || "—"}</TableCell>
                      <TableCell className="text-xs">{c.claimed_by_user_name || "—"}</TableCell>
                      <TableCell className="text-xs">{fmt(c.claim_timestamp)}</TableCell>
                      <TableCell className="text-[11px] text-slate-500">
                        {c.claim_gps_lat != null && c.claim_gps_lng != null
                          ? `${Number(c.claim_gps_lat).toFixed(4)}, ${Number(c.claim_gps_lng).toFixed(4)}`
                          : (c.claim_ip || "—")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateBoxDialog({ open, onClose, distributors, onCreate }) {
  const [did, setDid] = useState("");
  useEffect(() => { if (open) setDid(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Production Box</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">A unique box number (e.g. BOX000001) is generated automatically. You can assign the distributor now or later.</p>
        <Label className="mt-2">Distributor (optional)</Label>
        <Select value={did} onValueChange={setDid}>
          <SelectTrigger data-testid="create-box-dist"><SelectValue placeholder="Assign later" /></SelectTrigger>
          <SelectContent>
            {distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={GOLD_BTN} onClick={() => onCreate(did || null)} data-testid="create-box-confirm">Create Box</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignCouponsDialog({ box, batches, onClose, onDone }) {
  const [batchId, setBatchId] = useState("");
  const [fromS, setFromS] = useState("");
  const [toS, setToS] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (box) { setBatchId(""); setFromS(""); setToS(""); } }, [box]);

  const submit = async () => {
    if (!fromS.trim() || !toS.trim()) { toast.error("Enter From and To serial"); return; }
    setBusy(true);
    try {
      const r = await dms.cpnBoxAssignCoupons(box.id, {
        batch_id: batchId || undefined,
        from_serial: fromS.trim().toUpperCase(),
        to_serial: toS.trim().toUpperCase(),
      });
      toast.success(`${r.assigned} coupons assigned to ${box.box_number}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!box} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Assign Coupons → {box?.box_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Only <b>ACTIVE</b> coupons in the range are attached to this box.</p>
        <Label className="mt-2">Batch (optional filter)</Label>
        <Select value={batchId} onValueChange={setBatchId}>
          <SelectTrigger data-testid="assign-batch"><SelectValue placeholder="Any batch" /></SelectTrigger>
          <SelectContent>
            {(batches || []).map(b => (
              <SelectItem key={b.id} value={b.id}>{b.batch_label} · {b.coupon_type} · {b.count}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label>From Serial</Label>
            <Input value={fromS} onChange={e => setFromS(e.target.value)} placeholder="ABCDE001"
                   className="font-mono" data-testid="assign-from" />
          </div>
          <div>
            <Label>To Serial</Label>
            <Input value={toS} onChange={e => setToS(e.target.value)} placeholder="ABCDE020"
                   className="font-mono" data-testid="assign-to" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={GOLD_BTN} disabled={busy} onClick={submit} data-testid="assign-confirm">
            {busy ? "Assigning…" : "Assign to Box"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function OwnerDeletedItemsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreBusyKey, setRestoreBusyKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      dms.cpnListDeletedItems().then(r => r.data || []),
      dms.listDeletedRetailers()
        .then(r => (r.data || []).map(x => ({
          type: "retailer", id: x.id, label: x.name || x.id,
          title: x.phone ? `Retailer - ${x.phone}` : "Retailer",
          count: 1, deleted_at: x.deleted_at, deleted_by_name: x.deleted_by_name,
        })))
        .catch(() => []),
    ])
      .then(([a, b]) => setItems([...a, ...b]))
      .catch(() => toast.error("Failed to load deleted items"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (item) => {
    const key = `${item.type}:${item.id}`;
    setRestoreBusyKey(key);
    try {
      if (item.type === "retailer") { await dms.restoreRetailer(item.id); }
      else { await dms.cpnRestoreDeletedItem(item.type, item.id); }
      toast.success(`${item.label} restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setRestoreBusyKey(null); }
  };

  return (
    <div>
      <PageHeader
        title="Recently Deleted"
        subtitle="Everything deleted anywhere in the system, by anyone — restorable here for 30 days."
      />
      <Card className="overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2">
          <Trash2 size={16} /> Deleted Items
          <span className="ml-auto text-xs text-slate-500">{items.length} total</span>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Type</TableHead><TableHead>Label</TableHead>
            <TableHead>Details</TableHead><TableHead className="text-right">Count</TableHead>
            <TableHead>Deleted On</TableHead><TableHead>Deleted By</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">
                Nothing deleted right now
              </TableCell></TableRow>
            )}
            {items.map(item => {
              const key = `${item.type}:${item.id}`;
              return (
                <TableRow key={key} className="hover:bg-rose-50/30">
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.type === "batch" ? "bg-emerald-100 text-emerald-800" : item.type === "retailer" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>
                      {item.type === "batch" ? "COUPON BATCH" : item.type === "retailer" ? "RETAILER" : "BOX"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs font-semibold">{item.label}</TableCell>
                  <TableCell className="text-xs text-slate-600">{item.title || "—"}</TableCell>
                  <TableCell className="text-right">{item.count?.toLocaleString?.() || item.count || 0}</TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(item.deleted_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{item.deleted_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={restoreBusyKey === key}
                            onClick={() => restore(item)}
                            data-testid={`deleted-restore-${item.type}-${item.label}`}>
                      {restoreBusyKey === key
                        ? <><Loader2 size={14} className="mr-1 animate-spin" /> Restoring…</>
                        : <><RefreshCw size={14} className="mr-1" /> Restore</>}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// BATCH SCAN (Salesperson + Distributor): retailer lock -> many coupons -> Confirm
// ─────────────────────────────────────────────────────────────────────────
function useScanCtx() {
  const [gps, setGps] = useState(null);
  const deviceId = useMemo(() => {
    try {
      let d = window.localStorage.getItem("gooil_device_id");
      if (!d) {
        d = "dev-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.localStorage.setItem("gooil_device_id", d);
      }
      return d;
    } catch { return null; }
  }, []);
  useEffect(() => {
    try {
      navigator.geolocation?.getCurrentPosition(
        p => setGps({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {}, { timeout: 8000 });
    } catch { /* noop */ }
  }, []);
  return { gps, deviceId };
}

export function SalesOfficerScanPage() {
  const [retailers, setRetailers] = useState([]);
  const { gps, deviceId } = useScanCtx();
  useEffect(() => {
    dms.cpnSoRetailers().then(r => setRetailers(r.data || [])).catch(() => setRetailers([]));
  }, []);
  return (
    <div>
      <PageHeader title="Scan Coupons"
        subtitle="Select a retailer, scan as many coupons as needed, then Confirm — the total is credited to that retailer." />
      <BatchScan retailers={retailers} gps={gps} deviceId={deviceId}
                 previewFn={dms.cpnScanPreview} submitFn={dms.cpnScan} />
    </div>
  );
}

export function DistributorScanPage() {
  const [retailers, setRetailers] = useState([]);
  const { gps, deviceId } = useScanCtx();
  useEffect(() => {
    dms.listRetailers({ lite: 1 })
      .then(r => setRetailers(Array.isArray(r) ? r : (r.data || r.items || r.retailers || [])))
      .catch(() => setRetailers([]));
  }, []);
  return (
    <div>
      <PageHeader title="Scan Coupons"
        subtitle="Select your retailer, scan coupons, then Confirm — the total is credited to that retailer's wallet/ledger." />
      <BatchScan retailers={retailers} gps={gps} deviceId={deviceId}
                 previewFn={dms.cpnDistScanPreview} submitFn={dms.cpnDistScan} />
    </div>
  );
}
